import { useCallback, useRef, useState, useEffect } from 'react';
import { API_CONFIG, apiUrl } from '../config/api.js';
import { getSocket } from '../services/socketClient.js';
import { playChunk, playChunkGapless, partitionQueueAtCardBoundary } from '../components/player/v2/playbackDriver/audioPlayer.js';
import { GaplessScheduler } from '../components/player/v2/playbackDriver/gaplessScheduler.js';
import { AsyncAudioQueue } from '../components/player/v2/playbackDriver/asyncAudioQueue.js';
import { pbTrace } from '../components/player/v2/playbackDriver/playbackTrace.js';
import { addAudioBreadcrumb, recordAudioMeasurement } from '../monitoring/audioTelemetry.js';

/**
 * Create an Audio element with iOS Safari compatibility attributes.
 * iOS Safari requires playsinline to prevent fullscreen mode and
 * to properly handle autoplay restrictions.
 */
const createIOSCompatibleAudio = () => {
  const audio = new Audio();
  // iOS Safari requires these attributes for proper playback
  audio.setAttribute('playsinline', '');
  audio.setAttribute('webkit-playsinline', '');
  // Prevent iOS from trying to AirPlay by default
  audio.setAttribute('x-webkit-airplay', 'deny');
  // Preload metadata for faster start
  audio.preload = 'auto';
  return audio;
};

// Browser autoplay-permission primer. Intentionally module-level (page/tab
// scoped, NOT per-user): the silent-unlock element + AudioContext grant the tab
// autoplay permission once, and that permission must survive user/route changes.
// This is deliberately distinct from the per-user playback state, which lives on
// an audioSession (see createAudioSession) owned by UserAudioQueueProvider.
let sharedAudioElement = null;
let audioUnlocked = false;

const SKIP_REFETCH_DELAY_MS = 1500; // Ignore fetches for 1.5s after skip

const isCombatRoundAudioScope = (scopeRef) => (
  typeof scopeRef === 'string' && /:round:\d+$/.test(scopeRef)
);

/**
 * BOU-1324: per-user / per-campaign audio playback state.
 *
 * These fields were previously JS module-level `global*` singletons shared
 * process-wide across every hook mount, route, and user — the root of
 * cross-user / cross-scope audio bleed. They now live on a session object owned
 * by UserAudioQueueProvider: one session per route mount, reset on user change,
 * torn down on unmount. Because the provider is the single instance that wraps
 * all consumers, they still share exactly one queue + one <audio> element (no
 * double playback). The hook self-creates a session when none is passed
 * (standalone / test usage), keeping each instance isolated.
 */
export function createAudioSession() {
  return {
    // BOU-1324 (review P1): monotonic scope token bumped on every reset. A fetch
    // captures it before its awaited GET and re-checks after; if it changed, a
    // reset (user/campaign switch or unmount) happened mid-flight and the stale
    // response must be dropped — otherwise the prior user's/campaign's chunks
    // would enqueue into the freshly-reset session and replay the leak this
    // change removes. resetAudioSession mutates the session in place (old fetch
    // closures keep the same object), so a plain field reset can't invalidate
    // them; a bumped generation can.
    generation: 0,
    // Monotonic owner token for async playback recovery. Unlike stopEpoch
    // (which invalidates fetches even for a preserved player_voice interrupt),
    // this advances only when the active playback owner is revoked or replaced.
    playbackOwnershipEpoch: 0,
    // Per-session ordering token for overlapping async foreground recovery calls.
    // Only an attempt that still owns the latest token may update the autoplay
    // latch after its resume/play promise settles.
    playbackRecoveryAttemptEpoch: 0,
    // Single shared <audio> element. Created lazily in playQueueChunks, reused
    // across chunks, and dropped on stop/skip/reset so a fresh element avoids
    // the BOU-934 corrupt-decoder window after a mid-play abort.
    audioElement: null,
    // BOU-1809: Web Audio gapless scheduler (lazily created). When its
    // AudioContext is running (post user-gesture unlock) narration segments play
    // through it back-to-back with no inter-segment gap.
    scheduler: null,
    // Engine currently responsible for the queue batch. Foreground recovery
    // resumes only this owner so Web Audio and the element fallback cannot
    // overlap or restart one another.
    currentPlaybackEngine: null,
    // BOU-1977: once Web Audio preparation/scheduling proves unavailable, keep
    // subsequent batches on the element path for this session. This prevents a
    // single batch from alternating engines with different completion semantics.
    forceElementPlayback: false,
    // Wakeable FIFO queue — chunks play in backend order, and a late enqueue can
    // resume a consumer that is waiting for the active Web Audio timeline to drain.
    playbackQueue: new AsyncAudioQueue(),
    // Processing lock: only one processQueue loop runs at a time.
    isProcessing: false,
    // Dedup set of server-issued queue_ids already queued/played.
    queuedChunkIds: new Set(),
    // BOU-1852: Map of queue_id -> card_id for gapless segments whose onStart has fired (so
    // playChunkGapless already resolved and the loop moved on) but whose onEnded
    // has not — their deferred played-ACK is still pending. If a stop lands first,
    // scheduler.stop() nulls source.onended so onEnded never runs; stopQueuePlayback
    // acks these on the way down so a stopped row is not left unplayed-but-deduped
    // (which would stall the backend strict-prefix cursor).
    startedGaplessQueueIds: new Map(),
    currentRequestId: null,
    // Lazily populated per-request completion bookkeeping. Declared here so
    // createAudioSession remains the canonical list for reset invariants.
    requestPlayedChunks: null,
    requestCompletedIds: null,
    // Reveal-on-audio-start card transition guard.
    lastAudioStartCardId: null,
    // Group/queue id that currently owns audible playback. The queue id prevents
    // a later scheduled chunk from overwriting or clearing an earlier source that
    // is still audible (notably player_voice across a gapless group boundary).
    currentPlaybackGroup: null,
    currentPlaybackQueueId: null,
    // A new-turn interrupt can preserve an audible player_voice source while
    // canceling later scheduled groups. The processor holds its lock until that
    // source drains so fresh turn audio cannot overlap it.
    preservePlayerVoiceUntilEnd: false,
    // A new-turn interrupt can also preserve a player_voice tail that has been
    // pulled into the active batch behind another playback group.
    activePlaybackChunks: null,
    activePlaybackIndex: null,
    // False until the indexed chunk's onPlaying callback fires, so an
    // interrupt can retain a player_voice chunk that is still preparing.
    activePlaybackStarted: false,
    preservedPlayerVoicePending: false,
    // BOU-751: card_id of the last chunk we sent a deliver-ack for, so the
    // deliver-ack POST fires once per card transition (not per chunk).
    lastDeliveredCardId: null,
    // BOU-751: empty-queue poll timer; re-fetches while hasMore until chunks land.
    emptyQueuePollTimer: null,
    // Fetch lock + most-recent queued fetch (prevents concurrent GETs racing
    // duplicate chunks).
    isFetching: false,
    pendingFetch: null,
    // BOU-1275: performance.mark name for time-to-first-audio; consumed once/turn.
    ttfaStartMark: null,
    // TTFA decomposition. A bare duration cannot distinguish a slow pipeline
    // from a wake whose fetch missed and had to wait for a retry, and those want
    // opposite fixes. Counted only while a TTFA mark is pending.
    ttfaFetchAttempts: 0,
    ttfaFirstFetchHit: null,
    // An `audio_available` wake is positive evidence that audio exists. If its
    // fetch comes back empty the audio is merely not visible yet, so keep
    // looking on the short cadence until this bounded window closes.
    wakeRetryUntil: null,
    // Skip timestamp: ignore fetches for SKIP_REFETCH_DELAY_MS after a skip.
    skipTimestamp: 0,
    // BOU-1409: hard playback-suppression gate (cutscene hold). While true both
    // the fetch path and the audio_available wake are no-ops.
    playbackSuppressed: false,
    // BOU-1409: monotonic epoch bumped on each OFF->ON suppression edge so an
    // in-flight GET that spans a hold can be detected + dropped on resolve.
    suppressionEpoch: 0,
    // Combat narration is released only after the matching AI playback batch.
    // While set, queue rows and socket wakes must carry this exact server-owned
    // scope_ref (the durable combat narration_key). This keeps a slow prior
    // round's TTS from leaking through when the next round lowers the UI hold.
    audioScopeRef: null,
    // Invalidates an in-flight queue GET when a newer round changes the required
    // scope before that response resolves.
    audioScopeEpoch: 0,
    // BOU-1266/BOU-1275: set when audio.play() was rejected (autoplay blocked);
    // suppresses the post-playback resync loop until the user interacts.
    autoplayBlocked: false,
    // BOU-1925: set true after the first segment has ACTUALLY started playing this
    // session, i.e. autoplay is confirmed unblocked. Gates the dequeue-time
    // next-segment lookahead: at dequeue we cannot yet know whether THIS chunk
    // will be autoplay-blocked (that is only known once play() resolves/rejects),
    // so we only fire the early lookahead once some audio has demonstrably played.
    // (Distinct from the module-level `audioUnlocked` autoplay-permission primer.)
    firstPlaySucceeded: false,
    // BOU-1609: diagnostic timing marks (console.debug-gated, see logAudioTiming).
    // lastChunkEndedAt — performance.now() of the previous chunk's `ended`, used
    //   to measure the inter-segment gap actually experienced by the player
    //   (gap from chunk N `ended` to chunk N+1 `playing`). This is the
    //   client-perceived stutter (BOU-1609 hypothesis #3 / overall symptom).
    // queueDrainedAt — performance.now() when the processor drained the queue
    //   with more audio still expected; paired with the next chunk-available
    //   mark to measure how long the queue sat empty (poll/socket-wake gap).
    lastChunkEndedAt: null,
    queueDrainedAt: null,
    // BOU-1609 follow-up: performance.now() ceiling of the card-boundary window.
    // Stamped alongside queueDrainedAt (same guard: more cards expected, not
    // gated) and consumed by cardBoundaryWindowOpen() to keep the SHORT retry
    // cadence armed across the boundary instead of dropping to the 4s keepalive.
    cardBoundaryUntil: null,
    // BOU-1652: performance.now() ceiling of the post-drain "request still
    // producing" bridge — set while playing a not-yet-completed request's chunk
    // so the short retry cadence survives the brief gap between a SHORT segment
    // ending (isProcessing flips false) and its successor arriving. Auto-expires
    // so it can never busy-loop while idle.
    producingUntil: null,
    // BOU-1652 (review C3408397098): true while the chunk currently playing
    // belongs to a request that is still producing more segments. Gated by
    // isProcessing in audioStillExpected(), it keeps the during-playback retry
    // alive for the WHOLE segment (not just a fixed grace window), so a slow
    // successor to a long segment is still discovered before the segment ends.
    activeRequestProducing: false,
    // BOU-1652 (review C3408397100): increments on every stopQueuePlayback (skip,
    // manual card advance, or teardown). A lookahead GET captures it at fetch
    // start; an identity change by resolve time means a stop/advance happened
    // mid-flight, so the (now-stale) response must be dropped — not just skips.
    stopEpoch: 0,
    lastNewTurnStopEpoch: null,
    // Most recent stop that must invalidate in-flight audio rather than carry
    // player voice across a turn boundary (skip, manual advance, teardown).
    lastNonPreservingStopEpoch: null,
    // The latest named stop is paired with its epoch so a later bare stop
    // cannot clobber the card identity needed by an in-flight chunk settlement.
    lastNamedStopEpoch: 0,
    lastNamedStopCardId: null,
  };
}

// BOU-1609: console.debug-gated diagnostic timing mark, matching the existing
// audio-debug pattern in this file (console.debug('🎵 [USER_QUEUE] ...')). These
// are verbose-level only so they don't spam the default console, but are
// available on a real stuttering session to attribute the inter-segment gap.
function logAudioTiming(label, fields) {
  try {
    console.debug(`🎵 [AUDIO_TIMING] ${label}`, fields);
  } catch {
    // Never let instrumentation break playback.
  }
}

/**
 * BOU-1324: tear an audio session down to its initial state. Pauses + releases
 * the shared <audio> element (so a discarded session cannot keep playing),
 * cancels the empty-queue poll timer, and resets all playback fields. Idempotent
 * and null-safe. Called by UserAudioQueueProvider on user change (account switch)
 * and on unmount (route exit). The React `isPlaying` / `audioBlocked` flags are
 * reset separately by the hook's teardown callback.
 */
export function resetAudioSession(session) {
  if (!session) return;
  // BOU-1324 (review P1): bump (never reset to 0) so any fetch in flight detects
  // the reset after its await and drops its now-stale response.
  session.generation = (session.generation || 0) + 1;
  session.playbackOwnershipEpoch = (session.playbackOwnershipEpoch || 0) + 1;
  session.playbackRecoveryAttemptEpoch = 0;
  if (session.audioElement) {
    try {
      session.audioElement.pause();
      session.audioElement.removeAttribute('src');
      session.audioElement.load();
    } catch {
      // Ignore teardown errors; the reference is dropped regardless.
    }
    session.audioElement = null;
  }
  // BOU-1809: stop any scheduled Web Audio segments but KEEP the scheduler (and
  // its unlocked AudioContext) — dropping it would lose the user-gesture unlock
  // and force a fallback to the element path for the rest of the session.
  if (session.scheduler) {
    try {
      session.scheduler.stop();
    } catch {
      // Ignore teardown errors.
    }
  }
  session.forceElementPlayback = false;
  session.currentPlaybackEngine = null;
  if (session.emptyQueuePollTimer) {
    clearTimeout(session.emptyQueuePollTimer);
    session.emptyQueuePollTimer = null;
  }
  session.playbackQueue.reset();
  session.isProcessing = false;
  session.queuedChunkIds = new Set();
  // BOU-1852: drop (do NOT ack) started-gapless segments on teardown — matching
  // the BOU-1324 rule that a session reset must not advance the departed user's
  // playback state. stopQueuePlayback (skip/advance/interrupt) acks them instead.
  session.startedGaplessQueueIds = new Map();
  session.currentRequestId = null;
  session.currentPlaybackGroup = null;
  session.currentPlaybackQueueId = null;
  session.preservePlayerVoiceUntilEnd = false;
  session.activePlaybackChunks = null;
  session.activePlaybackIndex = null;
  session.activePlaybackStarted = false;
  session.preservedPlayerVoicePending = false;
  session.lastDeliveredCardId = null;
  session.isFetching = false;
  session.pendingFetch = null;
  session.ttfaStartMark = null;
  session.ttfaFetchAttempts = 0;
  session.ttfaFirstFetchHit = null;
  session.wakeRetryUntil = null;
  session.skipTimestamp = 0;
  session.playbackSuppressed = false;
  session.suppressionEpoch = 0;
  session.audioScopeRef = null;
  session.audioScopeEpoch = (session.audioScopeEpoch || 0) + 1;
  session.autoplayBlocked = false;
  session.firstPlaySucceeded = false;
  // BOU-1609: clear diagnostic timing marks on reset.
  session.lastChunkEndedAt = null;
  session.queueDrainedAt = null;
  session.cardBoundaryUntil = null;
  // BOU-1652: clear the producing window/flag so a new session can't inherit a
  // stale fast-poll cadence from the departed one. stopEpoch intentionally is NOT
  // reset — it must keep advancing so an in-flight GET from the old session is
  // still seen as stopped-mid-fetch.
  session.producingUntil = null;
  session.activeRequestProducing = false;
    session.lastNewTurnStopEpoch = null;
    session.lastNonPreservingStopEpoch = null;
  session.lastNamedStopEpoch = 0;
  session.lastNamedStopCardId = null;
  // Inc-2: clear per-request completion tracking maps on session reset.
  session.requestPlayedChunks = null;
  session.requestCompletedIds = null;
  // Reveal-on-audio-start: clear the last-started card so the next session's
  // first card fires onCardAudioStart.
  session.lastAudioStartCardId = null;
}

function namedStopCardIdAfterEpoch(session, chunkStopEpoch) {
  return session.lastNamedStopEpoch > chunkStopEpoch
    ? session.lastNamedStopCardId
    : null;
}

// BOU-780 followup: zeroed out — the 120ms gap stacked on top of an
// awaited mark-played HTTP POST and a fresh per-chunk audio download
// produced 300-800ms of dead air between every chunk. Removed entirely
// in favor of immediate transition; group boundaries still get a brief
// pause via INTER_GROUP_DELAY_MS below.
const INTER_CHUNK_DELAY_MS = 0;
const INTER_GROUP_DELAY_MS = 200; // Delay between playback groups for natural pacing
const EMPTY_QUEUE_POLL_MS = 4000;              // BOU-913: keepalive cadence (socket connected)
const EMPTY_QUEUE_POLL_MS_DISCONNECTED = 1000; // BOU-913: faster fallback when socket is down
// BOU-1652 (review C3408323694): while we're still waiting on the next segment
// of an in-flight narrator response, poll on a much shorter cadence than the idle
// keepalive. The lookahead fired on `playing` can return empty if the next
// segment isn't generated yet; a 4s wait would let a short segment 0 end before
// the retry, re-opening the first-segment gap. Self-cancels the moment a chunk
// lands, and bounded by PRODUCING_GRACE_MS below so it never busy-loops.
const IN_PLAYBACK_LOOKAHEAD_POLL_MS = 750;
// BOU-1652 (review C3408343362 / C3408343366): "more audio is still expected" can
// NOT be read from card-level has_more alone — a narrator response is usually a
// SINGLE card whose has_more is already false while its later same-card TTS
// segments are still being produced. The queue chunk carries the request status,
// so when a chunk we just started playing belongs to a request that is not yet
// `completed`/`failed`, we mark "still producing" for a bounded window. That
// window (a) opens the during-playback lookahead for single/final-card responses
// and (b) keeps the short retry cadence alive across the brief drain between a
// short segment ending and its successor arriving — without ever busy-looping,
// because it auto-expires and is only refreshed by an actually-playing producing
// chunk.
const PRODUCING_GRACE_MS = 2500;
// BOU-2270: the window above is sized for a GUESS — the request-status heuristic,
// which only says "this request looked unfinished when it was served". But when
// the chunk we just started playing carries seq < total_chunks - 1, more segments
// are outstanding as a matter of FACT, and 2.5s is far too short a window to bet
// the recovery poll on.
//
// Prod (7d, span.op:audio.measure, 612 samples) measured the cost. Inter-segment
// gap p50/p95/max in ms, by segment index:
//
//     seq 1 (0->1)   n=188   19 / 7005 / 10243   <-- the reported hang
//     seq 2          n=140   18 /   20 /    21
//     seq 3+         n=140   18 /   19 /    21
//
// Every later boundary is flat ~19ms (one animation frame) at EVERY percentile
// including max, so the gapless scheduler is fine; the damage is all at seq 1 and
// it is bimodal — most openings sit on that same 19ms floor, the rest run out to
// 10.2s. A runway that is merely too short loses a race by a few hundred ms; it
// cannot produce 10s. That tail is the client having stopped looking, because for
// a single-card narrator response all three `audioStillExpected()` terms are dead
// shortly after segment 0: has_more is false (its later segments are not cards),
// isProcessing goes false when segment 0 ends, and this window shut at 2.5s —
// taking the 750ms recovery poll with it. Nothing then recovers the card except
// an unrelated later `audio_available`.
//
// So hold the window open long enough to actually recover when we KNOW segments
// are outstanding. Still bounded: a request whose audio never arrives stops
// polling rather than retrying forever, and the fact is re-checked on every
// segment, so this only ever extends while real audio keeps arriving.
const OUTSTANDING_SEGMENT_GRACE_MS = 20000;
// BOU-1609 follow-up: how long a CARD BOUNDARY keeps the short retry cadence
// armed. Sibling of OUTSTANDING_SEGMENT_GRACE_MS above, sized by the same rule:
// extend the window only when we hold a FACT, not a guess. The fact here is
// `has_more` — the view says more cards are still coming in this turn — and the
// queue just drained, so the player is hearing silence right now.
//
// Without this the boundary falls to the 4s keepalive. `fastRetry` reads
// isProcessing (false — the processor just stopped), producingWindowOpen (false
// — the card's last segment has seq === total - 1, so the long window was never
// stamped) and wakeWindowOpen (false). Every term is dead at exactly the moment
// the next card is due, and the post-playback resync that fires immediately at
// the boundary usually loses its race with the backend cursor advance, because
// that advance is driven by the played-ACK for the very chunk that just ended.
// A miss then costs a full 4s, and 2-3 misses stack.
//
// Prod (60d, app.audio_playback_requests + user_audio_queue, card-to-card gaps
// where the next card's audio already existed): in-game narration is bimodal —
// 53% of boundaries under 1s, then a second hump spanning 7-17s on ~30% of them
// (p90 13.8s, 33.9% over 5s). Direct NPC conversation, same client code, sits at
// p90 2.8s / 6.3% over 5s. The stalls correlate with SHORT previous cards
// (2.17 chunks avg vs 4.42 on clean transitions) — exactly the case where the
// next card's audio has had the least time to become servable, so the first
// refetch comes back empty and the 4s cadence takes over.
//
// Bounded, self-expiring, and cleared the moment chunks land, so a manual-mode
// boundary waiting on a user click cannot fast-poll for the whole reading time.
const CARD_BOUNDARY_GRACE_MS = 8000;
// TTFA: how long an `audio_available` wake keeps the SHORT retry cadence armed
// when its own fetch came back empty.
//
// At a turn's first wake `isProcessing` is false and no producing window is
// open, so `fastRetry` is false and `schedulePollIfHasMore` picks the 4s
// keepalive — and for a single-card narrator response `hasMore` is false too, so
// `audioStillExpected()` is false and it does not re-arm at all. Either way the
// wake's own evidence is discarded, and prod TTFA shows the cost: samples at
// 3941ms and 4663ms against a 499ms floor.
//
// The backend broadcasts `audio_available` only after the queue row is written,
// so an empty fetch immediately after one means "not visible to my cursor yet",
// not "nothing is coming". Bounded so audio that genuinely never arrives stops
// being chased, and cleared as soon as chunks land.
const WAKE_RETRY_WINDOW_MS = 3000;
// BOU-1652 (review C3408411508): a request is only PRODUCING more segments while
// pending/generating. The backend flips it to `generated` the moment total_chunks
// is set — i.e. production is finished — then later played/completed
// (backend/src/gaia/infra/audio/repositories/request_repo.py set_total_chunks).
// Listed positively (rather than enumerating done statuses) so any non-producing
// status — generated, played, completed, failed, or unknown — correctly stops the
// lookahead/fast-poll instead of churning empty GETs for a finished chunk.
const REQUEST_PRODUCING_STATUSES = new Set(['pending', 'generating']);


/**
 * Unlock audio playback by playing a silent audio on user interaction.
 * Call this on any user click/tap to ensure audio can play.
 *
 * iOS Safari is particularly strict about requiring user gestures for audio.
 * This function uses AudioContext (more reliable) with Audio element fallback
 * to "prime" the browser's audio context, allowing subsequent audio playback.
 */
export function unlockAudio() {
  if (audioUnlocked) return Promise.resolve(true);

  // Try AudioContext first - more reliable across browsers
  const tryAudioContext = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return Promise.resolve(false);

      const ctx = new AudioContext();

      // If already running, we're good
      if (ctx.state === 'running') {
        audioUnlocked = true;
        console.log('🎵 [AUDIO] Browser audio unlocked via AudioContext (already running)');
        return Promise.resolve(true);
      }

      // Try to resume the context (requires user gesture)
      return ctx.resume()
        .then(() => {
          // Create and play a silent buffer to fully unlock
          const buffer = ctx.createBuffer(1, 1, 22050);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.start(0);

          audioUnlocked = true;
          console.log('🎵 [AUDIO] Browser audio unlocked via AudioContext');
          return true;
        })
        .catch(async () => {
          // A context that could not resume never granted autoplay permission.
          // Release it before trying the element fallback so repeated gestures
          // cannot exhaust the browser's AudioContext limit.
          try { await ctx.close(); } catch { /* ignore teardown errors */ }
          return false;
        });
    } catch {
      return Promise.resolve(false);
    }
  };

  // Fallback to Audio element approach
  const tryAudioElement = () => {
    if (!sharedAudioElement) {
      sharedAudioElement = createIOSCompatibleAudio();
      // Use a tiny silent audio data URI (approximately 0.1s of silence)
      sharedAudioElement.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIiwijW80AAQAAAT/JJ5mn//t/5J//l/kn5J+Sf/5J/t//5f5J+Sf//+X+Sf5J5J+X/8k/ygAAANtVVVV//tQxA4AAADSAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
    }

    // iOS Safari requires load() before play() to recognize the user gesture
    sharedAudioElement.load();

    // Call play() immediately - don't await anything before this to keep gesture context active
    return sharedAudioElement.play()
      .then(() => {
        audioUnlocked = true;
        console.log('🎵 [AUDIO] Browser audio unlocked via Audio element');
        return true;
      })
      .catch((err) => {
        // On iOS, NotAllowedError means gesture wasn't recognized - may need another tap
        if (err.name === 'NotAllowedError') {
          console.warn('🎵 [AUDIO] Audio unlock requires user gesture (iOS restriction)');
        } else {
          console.warn('🎵 [AUDIO] Failed to unlock audio:', err.name, err.message);
        }
        return false;
      });
  };

  // Try AudioContext first, then fall back to Audio element
  return tryAudioContext().then(success => {
    if (success) return true;
    return tryAudioElement();
  });
}

/**
 * Custom hook for user-scoped audio queue playback
 *
 * Handles fetching and playing audio chunks from the user's queue.
 * This replaces connection-scoped playback with stable user-scoped queues.
 *
 * Features:
 * - SINGLETON: Global queue shared across all hook instances
 * - In-memory FIFO queue to prevent overlapping playback
 * - Sequential playback guaranteed per user
 * - Rapid triggers are queued, not dropped
 * - Handles browser autoplay restrictions
 * - Card boundary gating: In manual card advance mode, stops at card boundaries
 *
 * @param {Object} params - Configuration object
 * @param {Object} params.user - Auth user object (must have GAIA user_id or email)
 * @param {Object} params.audioStream - Audio stream context
 * @param {Object} params.apiService - API service instance
 * @param {Function} params.socketEmit - Optional Socket.IO emit function for WebSocket acknowledgment
 * @param {string} params.campaignId - Optional campaign ID for WebSocket acknowledgment
 * @param {Function} params.onRequestComplete - Optional callback(requestId) when all chunks of a request are played
 * @param {number} params.currentCardIndex - Current card index being displayed (for card boundary gating in manual mode)
 * @param {boolean} params.autoCardAdvancement - Whether auto card advancement is enabled
 * @returns {Object} Audio queue management interface
 */
export function useUserAudioQueue({
  user,
  audioStream,
  apiService,
  onRequestComplete,
  // Fired when a NEW card's audio STARTS playing (first chunk of that card).
  // Reveal is driven off this (not onRequestComplete) so a card is on screen
  // WHILE its narration plays, not one card behind it.
  onCardAudioStart,
  currentCardIndex,
  autoCardAdvancement = true,
  // BOU-751: when true, the hook polls the audio queue every
  // EMPTY_QUEUE_POLL_MS while the local queue is empty, so a card
  // whose audio finishes generating after the queue drains can
  // still auto-play without requiring a socket event. The view
  // (player or quest) supplies this from useCardPlayback.has_more.
  hasMore = false,
  // BOU-1324: per-user playback state. UserAudioQueueProvider passes a single
  // shared session (one per route mount); when omitted (standalone/test usage)
  // the hook self-creates one so each instance stays isolated.
  audioSession,
}) {
  // BOU-1324: resolve the active audio session. sessionRef.current always points
  // at it for use inside the stable callbacks below (the module-global era used
  // `global*` here, which bled across users/routes). When no session is passed we
  // lazily create a per-instance one.
  const localAudioSessionRef = useRef(null);
  if (localAudioSessionRef.current === null) {
    localAudioSessionRef.current = createAudioSession();
  }
  const sessionRef = useRef(localAudioSessionRef.current);
  sessionRef.current = audioSession ?? localAudioSessionRef.current;
  const processQueueRef = useRef(null);

  // Use refs for currentCardIndex and autoCardAdvancement to access latest values in callbacks
  const currentCardIndexRef = useRef(currentCardIndex);
  const autoCardAdvancementRef = useRef(autoCardAdvancement);
  const hasMoreRef = useRef(hasMore);
  const ackBufferRef = useRef({ played: [], delivered: [], abandoned: [] });
  // Immediate ACK requests can still be cancelled by page teardown. Keep their
  // immutable payloads until the request fulfills so pagehide can enqueue an
  // idempotent beacon copy of anything the backend has not confirmed yet.
  const unresolvedAckPayloadsRef = useRef(new Set());

  // BOU-1652: "is more audio still expected for this turn?" Three sources:
  //   - card-level has_more (more cards to reveal), OR
  //   - the active request is still producing AND we are actively playing it
  //     (review C3408397098): keeps the during-playback retry alive for the WHOLE
  //     segment, so a slow successor to a long segment is discovered before it
  //     ends — bounded by isProcessing, so it can't poll while idle, OR
  //   - the short post-drain producing bridge (PRODUCING_GRACE_MS) for the gap
  //     between a SHORT segment ending and its successor arriving.
  // Used by both the during-playback lookahead and the empty-queue poll cadence.
  // Routed through a ref so playQueueChunks (defined earlier) can call it without
  // a dep cycle.
  const producingWindowOpen = useCallback(() => (
    typeof performance !== 'undefined'
    && sessionRef.current.producingUntil != null
    && performance.now() < sessionRef.current.producingUntil
  ), []);
  // TTFA: a recent `audio_available` wake whose fetch has not yet produced
  // chunks. Bounded by WAKE_RETRY_WINDOW_MS and cleared the moment chunks land.
  const wakeWindowOpen = useCallback(() => (
    typeof performance !== 'undefined'
    && sessionRef.current.wakeRetryUntil != null
    && performance.now() < sessionRef.current.wakeRetryUntil
  ), []);
  // BOU-1609 follow-up: a card boundary we are currently sitting in — the queue
  // drained with more cards expected and nothing has landed since.
  const cardBoundaryWindowOpen = useCallback(() => (
    typeof performance !== 'undefined'
    && sessionRef.current.cardBoundaryUntil != null
    && performance.now() < sessionRef.current.cardBoundaryUntil
  ), []);
  const audioStillExpected = useCallback(
    () => (
      hasMoreRef.current
      || (sessionRef.current.isProcessing && sessionRef.current.activeRequestProducing)
      || producingWindowOpen()
      // TTFA: without this a single-card narrator response (has_more=false) never
      // re-arms after a missed wake, and the turn waits on an unrelated event.
      || wakeWindowOpen()
      // BOU-1609 follow-up (local review P2): the boundary window has to say
      // "audio is still expected", not just "poll faster", for the same reason
      // wakeWindowOpen does. schedulePollIfHasMore returns EARLY unless this
      // predicate holds, so a window consulted only for the interval is dead
      // weight at the one boundary where hasMore has already flipped false:
      // the penultimate -> final card. There the view can advance to the last
      // card (hasMore=false) before that card's audio is visible to the cursor,
      // every other term is false, and no poll is scheduled at all — the exact
      // stall this change exists to remove, on the last card of every turn.
      || cardBoundaryWindowOpen()
    ),
    [producingWindowOpen, wakeWindowOpen, cardBoundaryWindowOpen],
  );
  const audioStillExpectedRef = useRef(audioStillExpected);
  audioStillExpectedRef.current = audioStillExpected;

  useEffect(() => {
    currentCardIndexRef.current = currentCardIndex;
  }, [currentCardIndex]);

  useEffect(() => {
    autoCardAdvancementRef.current = autoCardAdvancement;
  }, [autoCardAdvancement]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Use global singletons instead of instance-specific refs
  // This ensures all audio from any component goes through the same queue
  // Note: Card audio completion is now handled via backend socket events (card_audio_complete)

  // Track if audio is blocked
  const [audioBlocked, setAudioBlocked] = useState(false);
  // Track if queue is currently playing (synced from session state)
  const [isPlaying, setIsPlaying] = useState(sessionRef.current.isProcessing);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // BOU-1324: full teardown — reset the session AND the React playing/blocked
  // flags. The provider calls this on user change (account switch) and on
  // unmount (route exit) so a discarded session can't keep playing and the next
  // user starts from a clean queue.
  const teardownAudioSession = useCallback(({
    preservePlaybackSuppression = false,
    closeScheduler = false,
  } = {}) => {
    const playbackWasSuppressed = sessionRef.current.playbackSuppressed;
    const suppressionEpoch = sessionRef.current.suppressionEpoch;
    const scheduler = closeScheduler ? sessionRef.current.scheduler : null;
    resetAudioSession(sessionRef.current);
    // User/campaign resets retain the unlocked scheduler. Only destruction of
    // the provider mount owns closing its AudioContext.
    if (scheduler) {
      sessionRef.current.scheduler = null;
      scheduler.close().catch((err) => {
        console.warn('[audio] failed to close gapless scheduler', err);
      });
    }
    // UI holds are owned outside the user/campaign playback session. An identity
    // reset must clear the departed queue without silently releasing a hold that
    // is still active in the mounted shell.
    if (preservePlaybackSuppression && playbackWasSuppressed) {
      sessionRef.current.playbackSuppressed = true;
      sessionRef.current.suppressionEpoch = suppressionEpoch;
    }
    setIsPlaying(false);
    setAudioBlocked(false);
  }, []);

  // Use refs for callbacks to avoid stale closure issues
  // When playQueueChunks runs in a while loop, it needs the latest callback
  const onRequestCompleteRef = useRef(onRequestComplete);

  useEffect(() => {
    onRequestCompleteRef.current = onRequestComplete;
  }, [onRequestComplete]);

  const onCardAudioStartRef = useRef(onCardAudioStart);
  useEffect(() => {
    onCardAudioStartRef.current = onCardAudioStart;
  }, [onCardAudioStart]);

  const flushAcks = useCallback(() => {
    const buf = ackBufferRef.current;
    if (!buf.played.length && !buf.delivered.length && !buf.abandoned.length) return;
    const payload = {
      played: buf.played.splice(0),
      delivered: buf.delivered.splice(0),
      abandoned: buf.abandoned.splice(0),
    };
    unresolvedAckPayloadsRef.current.add(payload);
    try {
      Promise.resolve(apiService.makeRequest('/api/audio/user/ack', payload, 'POST'))
        .then(() => {
          unresolvedAckPayloadsRef.current.delete(payload);
        })
        .catch(err => {
          console.warn('[audio] batched ACK failed', err);
        });
    } catch (err) {
      // Keep the payload unresolved so pagehide still has one final delivery
      // opportunity even if the normal request failed synchronously.
      console.warn('[audio] batched ACK failed', err);
    }
  }, [apiService]);

  const enqueueAck = useCallback((kind, queueId) => {
    if (!queueId) return;
    const buf = ackBufferRef.current;
    buf[kind].push(queueId);
    // BOU-1409 (review P2): flush immediately for all ACK kinds. The played-ACK now
    // DRIVES playback_advance — the backend advances PlaybackState off whole-card
    // completion, not the deliver-ack — so buffering it (the old behaviour, when
    // played was "informational only") would delay the next card's reveal + audio
    // fetch by up to ACK_FLUSH_MS (~1s), reintroducing the inter-card dead air the
    // deliver-immediate path was added to avoid. Deliver still flushes immediately to
    // keep card-highlight onset tight. Abandoned must also flush immediately because
    // its request-scoped suppression covers never-fetched sibling rows; played does not.
    // Multiple chunks coalesce in one POST when they land within the same tick; the
    // backend completion check is per-card regardless.
    flushAcks();
  }, [flushAcks]);

  useEffect(() => () => flushAcks(), [flushAcks]);

  useEffect(() => {
    const handlePageHide = () => {
      if (typeof navigator?.sendBeacon !== 'function') return;
      const buffered = ackBufferRef.current;
      const played = new Set(buffered.played);
      const delivered = new Set(buffered.delivered);
      const abandoned = new Set(buffered.abandoned);
      for (const payload of unresolvedAckPayloadsRef.current) {
        for (const queueId of payload.played) played.add(queueId);
        for (const queueId of payload.delivered) delivered.add(queueId);
        for (const queueId of payload.abandoned) abandoned.add(queueId);
      }
      if (!played.size && !delivered.size && !abandoned.size) return;

      const payload = JSON.stringify({
        played: [...played],
        delivered: [...delivered],
        abandoned: [...abandoned],
      });
      try {
        const queued = navigator.sendBeacon(
          apiUrl('/api/audio/user/ack'),
          new Blob([payload], { type: 'application/json' }),
        );
        if (!queued) console.warn('[audio] pagehide ACK beacon was not queued');
      } catch (err) {
        console.warn('[audio] pagehide ACK beacon failed', err);
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  // Stop queue playback and clear queue (called when synchronized stream starts or new request)
  // Also sets skip timestamp to prevent immediate refetch from replaying audio
  //
  // skipCardId (optional): card_id of the card the user is explicitly
  // skipping. Pre-loaded chunks for this same card are marked abandoned with
  // request-scoped suppression so never-fetched sibling rows are covered; played
  // does not provide that suppression. They stay in the dedup set so they don't
  // replay; chunks for any other card are released so they can be re-fetched and
  // played.
  // Without skipCardId we conservatively release all queued chunks (the
  // legacy behavior — needed to unblock downstream chunks).
  const stopQueuePlayback = useCallback((isSkip = false, skipCardId = null, options = {}) => {
    const wasProcessing = sessionRef.current.isProcessing;
    // BOU-1757: a new-turn interrupt (turn_started) must NOT abort the player's
    // own input echo (player_voice) for the turn that just ran. When combat
    // initiates fast, the combat-initiated first-combatant turn fires
    // turn_started while that echo is still playing; aborting it here is why
    // "player input is not narrated when entering combat" (a timing race — a
    // slow combat init lets the echo finish, hiding it). Let the echo finish;
    // the next turn's audio fetches fresh once it completes. Hard stops (skip,
    // suppression gate, session teardown) pass no newTurnInterrupt and still
    // interrupt everything.
    const preservePlayingPlayerVoice =
      options.newTurnInterrupt
      && sessionRef.current.currentPlaybackGroup === 'player_voice';
    const activePlaybackIndex = sessionRef.current.activePlaybackIndex;
    const activePlaybackStarted = sessionRef.current.activePlaybackStarted;
    // The active index points at the currently preparing chunk until its
    // onPlaying callback fires; include it in the preserved tail in that case.
    const activePlaybackTail = sessionRef.current.isProcessing
      && Array.isArray(sessionRef.current.activePlaybackChunks)
      ? sessionRef.current.activePlaybackChunks.slice(
        Number.isInteger(activePlaybackIndex)
          ? activePlaybackIndex + (activePlaybackStarted ? 1 : 0)
          : 0,
      )
      : [];
    const preserveQueuedPlayerVoice =
      options.newTurnInterrupt
      && (
        [...sessionRef.current.playbackQueue].some(
          (chunk) => chunk?.playback_group === 'player_voice',
        )
        || activePlaybackTail.some(
          (chunk) => chunk?.playback_group === 'player_voice',
        )
      );
    const preservePlayerVoice = preservePlayingPlayerVoice || preserveQueuedPlayerVoice;
    if (preservePlayerVoice) {
      console.log('🎵 [USER_QUEUE] Preserving player_voice echo across new-turn interrupt (BOU-1757/2266)');
    } else {
      console.log('🎵 [USER_QUEUE] Stopping queue playback | isSkip=%s skipCardId=%s', isSkip, skipCardId);
    }
    // BOU-1652 (review C3408397100): advance the stop epoch so any during-playback
    // lookahead GET in flight is invalidated on resolve — covers manual card
    // advance (stopQueuePlayback(false, fromCardId)), which has no skip window,
    // as well as skips and teardowns. Also clear the producing flag so the fast
    // retry cadence does not outlive this stop.
    const stopEpoch = sessionRef.current.stopEpoch + 1;
    sessionRef.current.stopEpoch = stopEpoch;
    sessionRef.current.lastNewTurnStopEpoch = options.newTurnInterrupt ? stopEpoch : null;
    if (!options.newTurnInterrupt) {
      sessionRef.current.lastNonPreservingStopEpoch = stopEpoch;
    }
    if (skipCardId != null) {
      sessionRef.current.lastNamedStopEpoch = stopEpoch;
      sessionRef.current.lastNamedStopCardId = skipCardId;
    }
    sessionRef.current.activeRequestProducing = false;
    // BOU-780 followup #3+#6: distinguish between "more chunks for the
    // card the user is skipping" (mark-played + dedup-keep so they don't
    // replay) and "downstream chunks for the next card" (release dedup
    // so they can re-fetch and play). The currently-playing chunk is
    // not in sessionRef.current.playbackQueue at this point — processQueue extracted
    // it; its mark-played fires from the AbortError handler.
    const preservedQueuedPlayerVoice = preserveQueuedPlayerVoice && !preservePlayingPlayerVoice
      ? activePlaybackTail.filter((chunk) => chunk?.playback_group === 'player_voice')
      : [];
    for (const queuedChunk of sessionRef.current.playbackQueue) {
      const queueId = queuedChunk?.queue_id;
      if (!queueId) continue;
      if (preservePlayerVoice && queuedChunk.playback_group === 'player_voice') {
        preservedQueuedPlayerVoice.push(queuedChunk);
        continue;
      }
      // Abandon on ANY stop that names the card — skip AND click-advance alike.
      // Both mean "the user is done with this card"; the original code acked
      // these played (kept-deduped) so they never replayed, and gating the
      // abandon on isSkip alone would RELEASE them on a click-advance instead,
      // re-opening the replay race the played-ack existed to prevent.
      if (skipCardId && queuedChunk.card_id === skipCardId) {
        enqueueAck('abandoned', queueId);
        flushAcks();
      } else {
        sessionRef.current.queuedChunkIds.delete(String(queueId));
      }
    }
    if (preservePlayerVoice) {
      // Keep split player_voice continuations and leave an empty queue waiter
      // active so an in-flight voice lookahead can still wake the processor.
      sessionRef.current.playbackQueue.replace(preservedQueuedPlayerVoice);
    } else {
      sessionRef.current.playbackQueue.reset();
    }
    sessionRef.current.currentRequestId = null;
    // BOU-751: reset card-boundary tracker so the next card transition
    // emits a fresh deliver-ack.
    sessionRef.current.lastDeliveredCardId = null;
    // BOU-1275: discard any pending TTFA start mark so the next turn starts fresh.
    sessionRef.current.ttfaStartMark = null;
    sessionRef.current.ttfaFetchAttempts = 0;
    sessionRef.current.ttfaFirstFetchHit = null;
    // TTFA: a stop ends the wake's claim that audio is imminent, so the fast
    // retry cadence must not outlive it.
    sessionRef.current.wakeRetryUntil = null;
    // BOU-1609 follow-up (local review P1): the card-boundary window survives a
    // normal card advance and dies on everything else.
    //
    // A manual advance arrives here as stopQueuePlayback(false, fromCardId) —
    // CardPlaybackNarrativeView.handleAdvance -> stopLocalPlayback({isSkip:
    // false, fromCardId}). If the queue had ALREADY drained and the user clicks
    // through before the next card is servable, that is precisely the boundary
    // silence this window exists to shorten: clearing it here would drop the
    // follow-up fetch back to the 4s keepalive and hand manual-advance players
    // the very stall this change removes.
    //
    // A skip is the opposite intent — the user is abandoning audio, so the fast
    // cadence must not outlive it. A bare stop (campaign switch, audio off,
    // suppression, teardown) is likewise not a boundary; resetAudioSession
    // clears the field outright on real teardowns.
    //
    // Local review P2: the caller states this intent EXPLICITLY via
    // options.cardAdvance. An earlier form derived it as `!isSkip &&
    // !!skipCardId`, but skipCardId's contract is "the card this stop came
    // from" — nothing prevents a non-advance stop from carrying one, and a
    // future call site that did would silently leave the 750ms cadence armed
    // with playback stopped. Intent that matters is passed, not inferred.
    if (options.cardAdvance) {
      // Codex review: ARM a fresh window, don't merely decline to clear one.
      // Preserving the existing deadline is not enough in the two commonest
      // manual cases, and both leave cardBoundaryWindowOpen() false:
      //   - the player reads for longer than CARD_BOUNDARY_GRACE_MS before
      //     clicking, so the deadline armed at drain has already expired;
      //   - the player clicks while the card is still playing, so the queue
      //     never drained and no deadline was ever armed.
      // The advance itself is the fact this window encodes — the player just
      // asked for the next card — so it is the right moment to (re)arm.
      if (typeof performance !== 'undefined') {
        sessionRef.current.cardBoundaryUntil = performance.now() + CARD_BOUNDARY_GRACE_MS;
      }
    } else {
      sessionRef.current.cardBoundaryUntil = null;
    }
    // BOU-751: cancel any pending empty-queue poll.
    if (sessionRef.current.emptyQueuePollTimer) {
      clearTimeout(sessionRef.current.emptyQueuePollTimer);
      sessionRef.current.emptyQueuePollTimer = null;
    }
    // Local review: make the advance re-arm SELF-SUFFICIENT. The cancel above is
    // unconditional, so arming cardBoundaryUntil alone leaves the window open
    // with no timer behind it — polling then resumes only if the caller happens
    // to fire its own fetch. handleAdvance does (BOU-1429's shared trigger), but
    // that is an unverified contract for every OTHER advance route: an
    // auto-advance on playback end, a keyboard/gesture advance, or an advance
    // that early-returns before the trigger would leave the window armed and
    // nothing scheduled, falling back to the 4s keepalive — the stall, silently.
    // Schedule the retry here so the mechanism does not depend on the caller.
    // A caller that does fetch simply cancels this timer on the way through.
    if (options.cardAdvance) {
      const advanceCampaignId = lastFetchCampaignIdRef.current;
      if (advanceCampaignId) {
        sessionRef.current.emptyQueuePollTimer = setTimeout(() => {
          sessionRef.current.emptyQueuePollTimer = null;
          // Codex review: stand down if a fetch is already in flight or queued.
          // This fallback exists to guarantee that SOME fetch follows the
          // advance; when one is already coming, that guarantee is met and
          // firing anyway is actively harmful. `pendingFetch` is a single slot
          // overwritten wholesale, and fetchUserAudioQueue applies `cardIndex` /
          // `autoCardAdvancement` only AFTER its isFetching early return — so a
          // richer advance fetch (carrying the next cardIndex) queued by
          // advanceAudio would be replaced by these bare options, and the replay
          // would keep the stale manual-gate cursor and gate away the next
          // card's chunks. It also spares slow ordinary advances a redundant
          // immediate replay. Nothing is lost by skipping: the in-flight fetch's
          // own empty branch re-arms the poll, and the boundary window is armed.
          if (sessionRef.current.isFetching || sessionRef.current.pendingFetch) return;
          fetchUserAudioQueueRef.current?.(advanceCampaignId, { preserveAudioScope: true });
        }, IN_PLAYBACK_LOOKAHEAD_POLL_MS);
      }
    }
    if (preservePlayingPlayerVoice) {
      sessionRef.current.preservePlayerVoiceUntilEnd = true;
      try {
        sessionRef.current.scheduler?.cancelPending?.({
          preservePlaybackGroup: 'player_voice',
        });
      } catch {
        // Ignore cancellation errors; the loop guard below still drops unscheduled tails.
      }
      return;
    }
    if (preserveQueuedPlayerVoice) {
      sessionRef.current.preservedPlayerVoicePending = true;
    } else {
      sessionRef.current.preservedPlayerVoicePending = false;
    }
    sessionRef.current.preservePlayerVoiceUntilEnd = false;
    sessionRef.current.playbackOwnershipEpoch =
      (sessionRef.current.playbackOwnershipEpoch || 0) + 1;
    // Stop current audio - removeAttribute+load forces an error/abort event
    // which settles any pending playback Promise immediately. Then drop the
    // singleton so the next playQueueChunks builds a fresh element.
    //
    // BOU-934 followup: Chrome's FFmpeg demuxer occasionally fails the very
    // next chunk after a mid-play abort sequence with
    //   code=4 PipelineStatus::DEMUXER_ERROR_COULD_NOT_OPEN
    // on byte-identical MP3s that played fine on a fresh element. Symptom in
    // the wild: Jim narration interrupted by Quest Board → next quest-giver
    // scene's audio fails silently until the player submits a turn (which
    // gives the element enough time to settle). Recreating the element after
    // a skip / external stop avoids the corrupt-decoder window entirely. The
    // unlock state lives on the separate `sharedAudioElement` + page-wide
    // AudioContext, so a new Audio() inherits autoplay permission.
    if (sessionRef.current.audioElement) {
      sessionRef.current.audioElement.pause();
      sessionRef.current.audioElement.removeAttribute('src');
      sessionRef.current.audioElement.load();
      sessionRef.current.audioElement = null;
    }
    // BOU-1809: stop any scheduled Web Audio segments immediately (skip / new-turn
    // interrupt). Keep the scheduler + its unlocked context for subsequent playback.
    if (sessionRef.current.scheduler) {
      try {
        sessionRef.current.scheduler.stop();
      } catch {
        // Ignore teardown errors.
      }
    }
    // BOU-1852 (review): a gapless segment whose onStart already fired (so
    // playChunkGapless resolved and the loop moved past it) but that has not yet
    // reached onEnded is not covered by the AbortError path — that acks the chunk
    // whose await is still PENDING (the NEXT, not-yet-started segment), not the one
    // that was audible. scheduler.stop() above just nulled its source.onended, so
    // its deferred played-ACK will never fire. Ack these started-but-uncompleted
    // segments abandoned when an explicit skip targets skipCardId: request-scoped
    // suppression covers never-fetched sibling rows; played does not. Other started
    // segments are acked played (and kept deduped) so the backend advances its
    // strict-prefix cursor past the partially-heard row instead of pinning on it
    // while the local dedup filters it out of the next refetch (which would stall
    // narration).
    const startedGaplessIds = sessionRef.current.startedGaplessQueueIds;
    if (startedGaplessIds && startedGaplessIds.size) {
      for (const [startedQueueId, startedCardId] of startedGaplessIds) {
        // Same rule as the queued loop above: any stop naming the card
        // abandons its started-but-unfinished segments (suppression covers the
        // request's never-fetched siblings); other cards' segments keep the
        // played safety ack.
        enqueueAck(
          skipCardId && startedCardId === skipCardId ? 'abandoned' : 'played',
          startedQueueId,
        );
      }
      flushAcks();
      startedGaplessIds.clear();
    }
    // Reset processing lock (will cause current loop to exit on next iteration)
    sessionRef.current.isProcessing = false;
    sessionRef.current.currentPlaybackEngine = null;
    sessionRef.current.currentPlaybackGroup = null;
    sessionRef.current.currentPlaybackQueueId = null;
    setIsPlaying(false);
    // If this is a skip (user clicked skip button), set timestamp to prevent refetch
    if (isSkip) {
      sessionRef.current.skipTimestamp = Date.now();
      console.log('🎵 [USER_QUEUE] Skip timestamp set, will ignore fetches for %dms', SKIP_REFETCH_DELAY_MS);
    }
    if (preserveQueuedPlayerVoice && !preservePlayingPlayerVoice && !wasProcessing) {
      const preserveGeneration = sessionRef.current.generation;
      setTimeout(() => {
        if (
          sessionRef.current.generation === preserveGeneration
          && !sessionRef.current.isProcessing
          && sessionRef.current.playbackQueue.length > 0
        ) {
          processQueueRef.current?.();
        }
      }, 0);
    }
  }, [enqueueAck, flushAcks]);

  // BOU-1409: raise/lower the hard playback-suppression gate. Raising it also stops
  // whatever is playing now, so a single call both halts current audio AND blocks
  // any later fetch/socket wake until it is lowered. Used by QuestPhaseRouter to
  // hold audio for the duration of the quest-giver cutscene spinner.
  //
  // Raising the gate must stop BOTH local audio paths (per stopLocalPlayback): the
  // user-queue loop AND the synchronized AudioStreamContext <audio> element. A
  // queue-only stop would let a stream that is mid-playback keep going behind the
  // spinner until it naturally ends (Codex review).
  const setPlaybackSuppressed = useCallback((suppressed, reason = 'unspecified') => {
    const next = Boolean(suppressed);
    // BOU-1409 (Codex): open a new suppression epoch on the OFF→ON edge so any
    // queue GET already in flight is invalidated even if the gate is lowered
    // again (Continue) before that GET resolves.
    if (next && !sessionRef.current.playbackSuppressed) {
      sessionRef.current.suppressionEpoch += 1;
    }
    sessionRef.current.playbackSuppressed = next;
    console.log('🎵 [USER_QUEUE] Playback suppression %s', next ? 'ON' : 'OFF');
    // BOU-1654: audio observability — owning hold + reason for the suppression edge.
    addAudioBreadcrumb('suppression', { suppressed: next, reason });
    if (next) {
      stopQueuePlayback();
      audioStream?.stopStream?.();
    }
  }, [stopQueuePlayback, audioStream]);

  // Reactively update global audio element volume and mute state, and mirror the
  // same onto the BOU-1809 Web Audio scheduler's gain node so a live slider/mute
  // change applies to whichever path is currently playing.
  useEffect(() => {
    const v = audioStream?.volumeLevel ?? 1;
    if (sessionRef.current.audioElement) sessionRef.current.audioElement.volume = v;
    sessionRef.current.scheduler?.setVolume(v);
  }, [audioStream?.volumeLevel]);

  useEffect(() => {
    const m = audioStream?.isMuted ?? false;
    if (sessionRef.current.audioElement) sessionRef.current.audioElement.muted = m;
    sessionRef.current.scheduler?.setMuted(m);
  }, [audioStream?.isMuted]);

  const resumeActivePlayback = useCallback(async ({
    reason,
    primeIfIdle = false,
  }) => {
    const session = sessionRef.current;
    const attemptGeneration = session.generation;
    const attemptOwnershipEpoch = session.playbackOwnershipEpoch;
    let attemptRecoveryEpoch = session.playbackRecoveryAttemptEpoch || 0;
    const claimRecoveryAttempt = () => {
      attemptRecoveryEpoch = (session.playbackRecoveryAttemptEpoch || 0) + 1;
      session.playbackRecoveryAttemptEpoch = attemptRecoveryEpoch;
    };
    // Read exactly once: this marker is the canonical owner for the attempt.
    const activeEngine = session.currentPlaybackEngine;
    const hasActiveEngine = activeEngine === 'gapless' || activeEngine === 'element';
    let attemptResource = activeEngine === 'gapless'
      ? session.scheduler
      : (activeEngine === 'element' ? session.audioElement : session.scheduler);
    const wasAutoplayBlocked = session.autoplayBlocked;
    const reportedEngine = activeEngine || (primeIfIdle ? 'gapless' : 'none');
    const stillOwnsRecoveryAttempt = () => (
      mountedRef.current
      && sessionRef.current === session
      && session.generation === attemptGeneration
      && session.playbackOwnershipEpoch === attemptOwnershipEpoch
      && session.playbackRecoveryAttemptEpoch === attemptRecoveryEpoch
    );
    const stillOwnsAttempt = () => (
      stillOwnsRecoveryAttempt()
      && (
        hasActiveEngine
          ? (
            session.isProcessing
            && session.currentPlaybackEngine === activeEngine
            && (
              activeEngine === 'gapless'
                ? session.scheduler === attemptResource
                : session.audioElement === attemptResource
            )
          )
          : (
            (session.currentPlaybackEngine ?? null) === null
            && session.scheduler === attemptResource
          )
      )
    );
    const schedulerState = (scheduler) => (
      !scheduler ? 'missing' : (scheduler.isRunning ? 'running' : 'not-running')
    );
    const elementState = (element) => {
      if (!element) return 'missing';
      if (element.ended) return 'ended';
      return element.paused ? 'paused' : 'playing';
    };

    let attempted = false;
    let success = false;
    let outcome = 'no-active-engine';
    let errorName = null;
    let beforeState = 'not-applicable';
    let afterState = 'not-applicable';

    try {
      if (session.playbackSuppressed) {
        outcome = 'suppressed';
      } else if (hasActiveEngine && !session.isProcessing) {
        beforeState = activeEngine === 'gapless'
          ? schedulerState(attemptResource)
          : elementState(attemptResource);
        afterState = beforeState;
        outcome = 'inactive';
      } else if (activeEngine === 'gapless' || (!activeEngine && primeIfIdle)) {
        if (!attemptResource && !activeEngine && primeIfIdle) {
          attemptResource = new GaplessScheduler();
          session.scheduler = attemptResource;
        }
        beforeState = schedulerState(attemptResource);
        if (!attemptResource) {
          const error = new Error('Active gapless scheduler is missing');
          error.name = 'PlaybackEngineMissingError';
          throw error;
        }
        attempted = true;
        claimRecoveryAttempt();
        await attemptResource.resume();
        afterState = schedulerState(attemptResource);
        if (!attemptResource.isRunning) {
          const error = new Error('AudioContext did not enter running state');
          error.name = 'AudioContextNotRunningError';
          throw error;
        }
        success = true;
        outcome = beforeState === 'running' ? 'already-running' : 'resumed';
      } else if (activeEngine === 'element') {
        const audioElement = attemptResource;
        beforeState = elementState(attemptResource);
        afterState = beforeState;
        if (!audioElement) {
          outcome = 'missing-resource';
        } else if (audioElement.ended) {
          outcome = 'ended';
        } else if (!audioElement.paused) {
          outcome = 'already-playing';
        } else if (!audioElement.currentSrc && !audioElement.src) {
          outcome = 'source-pending';
        } else {
          attempted = true;
          claimRecoveryAttempt();
          await audioElement.play();
          afterState = elementState(audioElement);
          success = true;
          outcome = 'resumed';
        }
      }

      if (success) {
        if (stillOwnsAttempt()) {
          session.autoplayBlocked = false;
          setAudioBlocked(false);
        } else {
          outcome = 'stale';
        }
      }
    } catch (error) {
      outcome = 'failed';
      errorName = error?.name || 'Error';
      if (stillOwnsAttempt()) {
        session.autoplayBlocked = true;
        setAudioBlocked(true);
      } else {
        outcome = 'stale';
      }
    } finally {
      addAudioBreadcrumb('visibility-resume', {
        reason,
        engine: reportedEngine,
        beforeState,
        afterState,
        outcome,
        errorName,
      });
    }

    return {
      attempted,
      success,
      outcome,
      wasAutoplayBlocked,
      isCurrent: stillOwnsAttempt,
      isCurrentRecoveryAttempt: stillOwnsRecoveryAttempt,
    };
  }, []);

  // BOU-1764: browsers may suspend the active playback engine while a tab is
  // backgrounded. Resume that same engine on foreground; normal audio callbacks
  // remain solely responsible for card advancement and queue completion.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      void resumeActivePlayback({
        reason: 'visibilitychange',
        primeIfIdle: false,
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [resumeActivePlayback]);

  const recoverAudio = useCallback(async (reason = 'user-interaction') => {
    const [resumeResult, unlocked] = await Promise.all([
      resumeActivePlayback({ reason, primeIfIdle: true }),
      unlockAudio(),
    ]);
    const session = sessionRef.current;
    const stillOwnsPlayback = resumeResult.isCurrent();
    const ownsBlockedIdleRecovery = (
      !stillOwnsPlayback
      && resumeResult.isCurrentRecoveryAttempt()
      && session.autoplayBlocked
      && !session.isProcessing
      && (session.currentPlaybackEngine ?? null) === null
    );
    if (!stillOwnsPlayback && !ownsBlockedIdleRecovery) return false;

    const audioIsUnlocked = Boolean(unlocked || resumeResult.success);
    if (!audioIsUnlocked) return false;

    const shouldRefetch = resumeResult.wasAutoplayBlocked || session.autoplayBlocked;
    setAudioBlocked(false);
    session.autoplayBlocked = false;
    if (shouldRefetch) {
      const campaignId = lastFetchCampaignIdRef.current;
      if (campaignId) {
        fetchUserAudioQueueRef.current?.(campaignId, { preserveAudioScope: true });
      }
    }
    return true;
  }, [resumeActivePlayback]);

  // Try to unlock audio on the first user interaction. Both calls begin inside
  // the gesture; the shared helper owns scheduler resumption so it cannot happen
  // twice through separate recovery paths.
  useEffect(() => {
    let handled = false;
    const handleInteraction = () => {
      if (handled) return;
      handled = true;
      void recoverAudio();
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('keydown', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [recoverAudio]);

  const registerQueueId = useCallback((queueId) => {
    if (!queueId) {
      return true;
    }
    const normalizedId = String(queueId);
    if (sessionRef.current.queuedChunkIds.has(normalizedId)) {
      return false;
    }
    sessionRef.current.queuedChunkIds.add(normalizedId);
    return true;
  }, []);

  const releaseQueueId = useCallback((queueId) => {
    if (!queueId) {
      return;
    }
    sessionRef.current.queuedChunkIds.delete(String(queueId));
  }, []);

  const preloadAudioChunks = useCallback((chunks) => {
    if (!Array.isArray(chunks) || chunks.length === 0) return;
    apiService.getAccessToken()
      .catch(() => null)
      .then((authToken) => {
        for (const chunk of chunks) {
          if (!chunk?.url) continue;
          let preloadUrl = chunk.url.startsWith('http')
            ? chunk.url
            : `${API_CONFIG.BACKEND_URL}${chunk.url}`;
          if (authToken) {
            preloadUrl += `?token=${encodeURIComponent(authToken)}`;
          }
          fetch(preloadUrl, { method: 'GET', cache: 'force-cache' }).catch(() => {
            // Best-effort cache warm; real playback fetch still handles failures.
          });
        }
      });
  }, [apiService]);

  // Play audio chunks sequentially from user queue
  const playQueueChunks = useCallback(async (chunks, onPlaybackOwnershipEstablished) => {
    if (!chunks || chunks.length === 0) {
      console.log('🎵 [USER_QUEUE] No chunks to play');
      return;
    }

    sessionRef.current.activePlaybackChunks = chunks;
    sessionRef.current.activePlaybackIndex = 0;
    sessionRef.current.activePlaybackStarted = false;
    const playStopEpoch = sessionRef.current.stopEpoch;
    const playOwnershipEpoch = sessionRef.current.playbackOwnershipEpoch;

    // BOU-1324 (review): snapshot the session generation at the start of this
    // play run. A teardown (user switch / unmount) bumps it and aborts playback
    // via isProcessing=false; on the AbortError path we use this to tell a
    // teardown-abort (must NOT ack the old session's chunk as played) apart from
    // a user-skip abort (which legitimately acks).
    const playGeneration = sessionRef.current.generation;
    const reconcileRevokedBatch = () => {
      const replacementOwnedChunks =
        sessionRef.current.isProcessing
        && sessionRef.current.playbackOwnershipEpoch !== playOwnershipEpoch
        && Array.isArray(sessionRef.current.activePlaybackChunks)
          ? sessionRef.current.activePlaybackChunks
          : [];
      const preservedQueueIds = new Set(
        [...sessionRef.current.playbackQueue]
          .concat(replacementOwnedChunks)
          .map((chunk) => chunk?.queue_id)
          .filter(Boolean)
          .map(String),
      );
      for (const drainedChunk of chunks) {
        const drainedQueueId = drainedChunk?.queue_id;
        if (drainedQueueId && !preservedQueueIds.has(String(drainedQueueId))) {
          sessionRef.current.queuedChunkIds.delete(String(drainedQueueId));
        }
      }
    };

    // BOU-2266 (review P2, local-review round 1): the pre-play cancellation
    // branch and the AbortError-path tail cleanup both need to know whether a
    // SPECIFIC queue_id is still legitimately alive somewhere else (still
    // sitting in the flat queue, or already claimed by a replacement's active
    // batch) before releasing it from the dedup set. A flag-and-group check
    // (`preservedPlayerVoicePending && chunk.playback_group === 'player_voice'`)
    // is both too early — processQueue clears that flag as soon as ANY
    // player_voice chunk appears in a fresh chunksToPlay batch, before that
    // batch's playQueueChunks run has actually established ownership — and too
    // broad — it protects every player_voice chunk while the flag happens to
    // be set, including ones that were never actually preserved. Query live
    // ownership instead, the same invariant reconcileRevokedBatch above already
    // uses: a chunk is safe from release iff it is still queued or owned by a
    // batch that isn't this run's own.
    const isPreservedElsewhere = (candidateChunk) => {
      const candidateId = candidateChunk?.queue_id != null ? String(candidateChunk.queue_id) : null;
      if (!candidateId) return false;
      const stillQueued = [...sessionRef.current.playbackQueue].some(
        (queued) => queued?.queue_id != null && String(queued.queue_id) === candidateId,
      );
      if (stillQueued) return true;
      const active = sessionRef.current.activePlaybackChunks;
      return (
        Array.isArray(active)
        && active !== chunks
        && active.some(
          (owned) => owned?.queue_id != null && String(owned.queue_id) === candidateId,
        )
      );
    };

    // Inc-2: per-request completion tracking (Map-based, cross-batch deduped).
    // Each distinct request_id in this batch gets its own played/total counter.
    // totalChunksForRequest is the count of THIS BATCH's chunks for that request_id;
    // played chunks accumulate across batches via sessionRef.current.requestPlayedChunks
    // (a Map<requestId, playedCount>) and fire onRequestComplete ONCE when genuinely
    // complete (playedChunks >= totalChunks from the chunk.total_chunks field).
    // The fired set (sessionRef.current.requestCompletedIds, a Set<requestId>) dedupes
    // cross-batch double-fires. Both are keyed on request_id which is stable across
    // batches for the same TTS request.
    if (!sessionRef.current.requestPlayedChunks) {
      sessionRef.current.requestPlayedChunks = new Map();
    }
    if (!sessionRef.current.requestCompletedIds) {
      sessionRef.current.requestCompletedIds = new Set();
    }
    // BOU-1852: lazy-init for sessions created before this field existed.
    if (!sessionRef.current.startedGaplessQueueIds) {
      sessionRef.current.startedGaplessQueueIds = new Map();
    }

    // Stop any synchronized audio stream to prevent overlapping playback
    if (audioStream?.isStreaming && audioStream?.stopStream) {
      console.log('🎵 [USER_QUEUE] Stopping active audio stream before queue playback');
      audioStream.stopStream();
    }

    // Get auth token once for all chunks
    let authToken = null;
    try {
      authToken = await apiService.getAccessToken();
    } catch (error) {
      console.error('🎵 [USER_QUEUE] Failed to get auth token:', error);
    }

    if (
      sessionRef.current.generation !== playGeneration
      || sessionRef.current.stopEpoch !== playStopEpoch
      || sessionRef.current.playbackOwnershipEpoch !== playOwnershipEpoch
    ) {
      reconcileRevokedBatch();
      return false;
    }

    console.log(`🎵 [USER_QUEUE] Starting sequential playback of ${chunks.length} chunks`);

    // Select one engine for the entire batch. A failed Web Audio batch downgrades
    // at a scheduler-drain boundary and requeues its unstarted suffix; it never
    // starts the element path inline and then switches back on a later segment.
    let playbackEngine = 'element';
    try {
      if (
        !sessionRef.current.forceElementPlayback
        && sessionRef.current.scheduler
      ) {
        await sessionRef.current.scheduler.resume();
        sessionRef.current.scheduler.setVolume(audioStream?.volumeLevel ?? 1);
        sessionRef.current.scheduler.setMuted(audioStream?.isMuted ?? false);
        if (sessionRef.current.scheduler.isRunning) playbackEngine = 'gapless';
      }
    } catch {
      sessionRef.current.forceElementPlayback = true;
    }

    // BOU-1764: visibility recovery must resume the engine that owns this exact
    // batch, never guess from whichever resources happen to exist on session.
    if (
      sessionRef.current.generation !== playGeneration
      || sessionRef.current.stopEpoch !== playStopEpoch
      || sessionRef.current.playbackOwnershipEpoch !== playOwnershipEpoch
    ) {
      reconcileRevokedBatch();
      return false;
    }
    sessionRef.current.playbackOwnershipEpoch =
      (sessionRef.current.playbackOwnershipEpoch || 0) + 1;
    sessionRef.current.currentPlaybackEngine = playbackEngine;
    // BOU-2266 (review P1): the epoch THIS run now owns, captured once so the
    // post-download ownership recheck below can detect a replacement processor
    // re-establishing ownership (bumping this same field again) while this run
    // is blocked on an awaited blob download. `playOwnershipEpoch` above is the
    // PRE-establishment snapshot — it always mismatches after this line, so it
    // cannot be reused for that later check.
    const establishedOwnershipEpoch = sessionRef.current.playbackOwnershipEpoch;
    onPlaybackOwnershipEstablished?.(establishedOwnershipEpoch);

    // The media element is a batch-level compatibility engine. Avoid creating or
    // mutating it during gapless playback so the two engines cannot overlap.
    let audio = null;
    if (playbackEngine === 'element') {
      if (!sessionRef.current.audioElement) {
        sessionRef.current.audioElement = createIOSCompatibleAudio();
        sessionRef.current.audioElement.volume = audioStream?.volumeLevel ?? 1;
        sessionRef.current.audioElement.muted = audioStream?.isMuted ?? false;
      }
      audio = sessionRef.current.audioElement;
    }

    const downgradeGaplessBatch = async (failedIndex, error) => {
      console.warn(
        '🎵 [USER_QUEUE] Gapless batch failed; replaying its unstarted suffix with the element engine:',
        error,
      );
      sessionRef.current.forceElementPlayback = true;

      // `processQueue` drained this batch before calling us. Put the failed
      // segment and its untouched suffix back ahead of chunks concurrently
      // appended by lookahead fetches, retaining every queue id in dedup state.
      const appendedChunks = sessionRef.current.playbackQueue.drain();
      const retryChunks = [
        ...chunks.slice(failedIndex),
        ...appendedChunks,
      ];
      if (sessionRef.current.preservePlayerVoiceUntilEnd) {
        const preservedVoiceChunks = retryChunks.filter(
          (retryChunk) => retryChunk?.playback_group === 'player_voice',
        );
        for (const discardedChunk of retryChunks) {
          if (discardedChunk?.playback_group === 'player_voice') continue;
          if (discardedChunk?.queue_id) {
            sessionRef.current.queuedChunkIds.delete(String(discardedChunk.queue_id));
          }
        }
        sessionRef.current.playbackQueue.replace(preservedVoiceChunks);
      } else {
        sessionRef.current.playbackQueue.replace(retryChunks);
      }

      await sessionRef.current.scheduler?.whenDrained?.();
      return sessionRef.current.generation === playGeneration
        && sessionRef.current.isProcessing
        && !sessionRef.current.playbackSuppressed;
    };

    for (let i = 0; i < chunks.length; i++) {
      sessionRef.current.activePlaybackIndex = i;
      sessionRef.current.activePlaybackStarted = false;
      const chunk = chunks[i];
      // A preserve interrupt may land before the next chunk has been scheduled,
      // while it is decoding, or after it is pending on the Web Audio timeline.
      // Continue only player_voice segments; release the first later group and
      // its tail, then hold the processor lock until the preserved voice drains.
      if (
        sessionRef.current.preservePlayerVoiceUntilEnd
        && chunk?.playback_group !== 'player_voice'
      ) {
        for (let j = i; j < chunks.length; j++) {
          const pendingQueueId = chunks[j]?.queue_id;
          if (pendingQueueId) {
            sessionRef.current.queuedChunkIds.delete(String(pendingQueueId));
          }
        }
        await sessionRef.current.scheduler?.whenDrained?.();
        sessionRef.current.preservePlayerVoiceUntilEnd = false;
        return true;
      }
      // Check if playback was cancelled (e.g., by skip audio)
      // stopQueuePlayback sets sessionRef.current.isProcessing = false
      if (!sessionRef.current.isProcessing) {
        console.log(`🎵 [USER_QUEUE] Playback cancelled, stopping at chunk ${i + 1}/${chunks.length}`);
        return false;
      }

      const queueId = chunk.queue_id ? String(chunk.queue_id) : null;
      const chunkStopEpoch = sessionRef.current.stopEpoch;
      // Preserve the pre-start player_voice protection for the first in-flight
      // chunk, but do not let later lookahead scheduling overwrite the group of
      // a source that is already audible.
      if (!sessionRef.current.currentPlaybackQueueId) {
        sessionRef.current.currentPlaybackGroup = chunk.playback_group ?? null;
      }
      console.log(`🎵 [USER_QUEUE] Playing chunk ${i + 1}/${chunks.length}:`, chunk.url);
      pbTrace('audio_play', {
        cardId: chunk.card_id ?? null,
        requestId: chunk.request_id ?? null,
        group: chunk.playback_group ?? null,
        seq: chunk.sequence_number ?? null,
        idx: `${i + 1}/${chunks.length}`,
      });

      // Reveal-on-audio-start fires from the onPlaying callback below — i.e. only
      // after play() actually SUCCEEDS — not here at dequeue. Firing here would
      // reveal the card (and, for a final card, clear driver.hasMore / unlock
      // input) before any narration is audible on a slow blob download or an
      // autoplay-blocked reload. [Codex 3462051237]

      // Validate URL before attempting playback. A malformed row with an
      // empty/missing URL is unplayable — treat it like the hard-failure path
      // below: ack it played (so the backend advances the strict-prefix
      // frontier / reaper past it) and KEEP it deduped (do NOT release) so the
      // post-stop resync can't re-serve the same dead row. Previously this
      // released the queue id and returned false, so processQueue's
      // stall-recovery resync (delayMs=0) immediately refetched + re-registered
      // the same bad row and re-entered processQueue — a tight API/playback
      // retry loop (BOU-1266). Skip to the next chunk instead of aborting the
      // whole batch so one bad row doesn't strand the good chunks behind it.
      if (!chunk.url || typeof chunk.url !== 'string' || chunk.url.trim() === '') {
        console.warn(`🎵 [USER_QUEUE] Skipping chunk ${i + 1}/${chunks.length}: invalid or empty URL (acking + skipping unusable row)`);
        if (queueId) {
          enqueueAck('played', queueId);
          flushAcks();
        }
        continue;
      }

      // BOU-751: card-boundary deliver-ack. The deliver-ack POST tells the
      // backend "we're starting card N's audio" so it advances
      // PlaybackState. Once we've ack'd a chunk for a given card_id, the
      // remaining chunks for that same card don't need to ack — the
      // advance is already done, and the backend would no-op anyway.
      // Sending one POST per card transition (instead of per chunk)
      // eliminates redundant broadcasts and re-fetches.
      const cardId = chunk.card_id ?? null;
      const isCardBoundary = cardId !== null && cardId !== sessionRef.current.lastDeliveredCardId;
      if (queueId && isCardBoundary) {
        sessionRef.current.lastDeliveredCardId = cardId;
        enqueueAck('delivered', queueId);
      }

      // BOU-1925: fire the next-segment discovery lookahead one step EARLIER —
      // here at dequeue, BEFORE this segment downloads/decodes/plays — so a short
      // opening segment (the "first couple words") does not drain the gapless
      // timeline before segment 1 has been fetched. The onPlaying lookahead below
      // remains the safe primary; this only gives the discovery fetch a head
      // start equal to this segment's download+decode time. "More expected" is
      // read straight off the chunk (sequence_number vs total_chunks) since the
      // onPlaying producing-window (producingUntil) has not been set yet at
      // dequeue. Gated on !autoplayBlocked so a not-yet-unlocked chunk never kicks
      // an append (the very reason the primary fires from onPlaying), and
      // coalesced/deduped by fetchUserAudioQueue so a redundant call no-ops.
      {
        const seq = Number(chunk.sequence_number);
        const total = Number(chunk.total_chunks);
        const moreSegmentsInRequest =
          Number.isFinite(seq) && Number.isFinite(total) && total > 0 && seq < total - 1;
        const requestProducing =
          moreSegmentsInRequest
          || (!!chunk.request_status && REQUEST_PRODUCING_STATUSES.has(chunk.request_status));
        const nothingBufferedAhead =
          i === chunks.length - 1 && sessionRef.current.playbackQueue.length === 0;
        if (
          nothingBufferedAhead &&
          (requestProducing || audioStillExpectedRef.current()) &&
          !sessionRef.current.playbackSuppressed &&
          !sessionRef.current.autoplayBlocked &&
          // Only once audio is confirmed playable this session — at dequeue we
          // can't yet know if THIS chunk will be autoplay-blocked, so firing
          // before any successful play would look ahead on a blocked chunk.
          sessionRef.current.firstPlaySucceeded
        ) {
          const lookaheadCampaignId = lastFetchCampaignIdRef.current;
          if (lookaheadCampaignId) {
            fetchUserAudioQueueRef.current?.(lookaheadCampaignId, { preserveAudioScope: true });
          }
        }
      }

      let blobUrlForChunk = null;
      // BOU-1809: the downloaded Blob, kept so the gapless path can read a fresh
      // ArrayBuffer for decodeAudioData (which detaches the buffer it is given —
      // .arrayBuffer() returns a new copy each call, so the Blob stays reusable
      // for the element fallback).
      let blobForChunk = null;
      let chunkStarted = false;
      try {
        // Prepend backend URL if chunk.url is relative
        let audioUrl = chunk.url.startsWith('http') ? chunk.url : `${API_CONFIG.BACKEND_URL}${chunk.url}`;

        // Append auth token as query parameter for authenticated access
        if (authToken) {
          audioUrl += `?token=${encodeURIComponent(authToken)}`;
        }

        if (playbackEngine === 'element') {
          try {
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
          } catch {
            // Ignore reset errors; assigning a new src below will still replace playback.
          }
        }

        // BOU-780 followup #7: download the entire MP3 upfront and play
        // from a Blob URL. The audio media endpoint serves StreamingResponse
        // without HTTP Range support, so the browser's audio element can't
        // reliably maintain a buffer ahead of playback for long single-chunk
        // narration (~30s / 500KB). Result: mid-playback stuttering as the
        // decoder waits on more bytes. Fetching to memory first eliminates
        // the streaming dependency and gives the browser the full file
        // before play() — small startup cost (one RTT, often cached from
        // the next-chunk preload below), no playback hiccups.
        try {
          const audioResponse = await fetch(audioUrl, { cache: 'force-cache' });
          if (!audioResponse.ok) {
            throw new Error(`Audio fetch failed: ${audioResponse.status}`);
          }
          const blob = await audioResponse.blob();
          blobForChunk = blob;
          // Defensive diagnostic: when audio decode fails on staging/prod, the
          // most common root cause is the response body not actually being an
          // MP3 — could be 0 bytes, an HTML SPA fallback, or a JSON error body
          // returned with a 200 status. Capture the shape now so a downstream
          // DEMUXER error has actionable context instead of an opaque code=4.
          // Stays warn-level (not error) so it only fires when something is
          // obviously off; the audio element's own onError still wins for the
          // real failure path.
          const blobIsAudio = blob.type && blob.type.startsWith('audio/');
          if (!blobIsAudio || blob.size < 64) {
            console.warn(
              `🎵 [USER_QUEUE] Suspect audio response — type=${blob.type || '(none)'} size=${blob.size} status=${audioResponse.status} url=${audioUrl.split('?')[0]}`,
            );
          }
          if (playbackEngine === 'element') {
            blobUrlForChunk = URL.createObjectURL(blob);
            audio.src = blobUrlForChunk;
            audio.load();
          }
        } catch (fetchErr) {
          // The element engine can still try direct streaming. A gapless batch
          // handles this below as a capability downgrade at a batch boundary.
          console.warn('🎵 [USER_QUEUE] Blob preload failed:', fetchErr);
          if (playbackEngine === 'element') {
            audio.src = audioUrl;
            audio.load();
          }
        }

        // BOU-1409 (Codex): suppression / cancellation can land mid-download. The
        // per-chunk blob fetch above is awaited, so a quest-board select or cutscene
        // hold that fired stopQueuePlayback (sessionRef.current.isProcessing=false) or raised the
        // suppression gate while we were downloading would NOT have stopped this
        // chunk — without this guard we'd assign src + play() below and restart
        // stale audio behind the spinner. Bail before play and release the dedup ids
        // for this chunk + the rest of the batch so the post-Continue resume fetch
        // can re-serve the now-revealed opening (mirrors the autoplay-blocked
        // release; the finally below revokes blobUrlForChunk). Nothing is marked
        // played — the held audio was never heard.
        // BOU-1324 (review): a teardown (user/campaign switch or unmount) during
        // the awaited blob download orphans this run. The shared isProcessing
        // flag is not enough — a fresh run for the new session can flip it back
        // to true before this stale download resolves, which would let this
        // closure play its orphaned old element into the new session. Re-check
        // the generation captured at the start of THIS run; if it changed, bail
        // without touching the new session's (already-reset) dedup set.
        if (sessionRef.current.generation !== playGeneration) {
          console.log(
            `🎵 [USER_QUEUE] Aborting chunk ${i + 1}/${chunks.length} before play — session torn down mid-download`
          );
          return false;
        }

        // BOU-2266 (review P1): `isProcessing` alone is not enough — a stop
        // during this download can be followed by a REPLACEMENT processor
        // (fresh queue fetch -> processQueue -> playQueueChunks) that starts
        // and flips the shared `isProcessing` flag back to true, and re-
        // establishes ownership (bumping playbackOwnershipEpoch again) before
        // this stale download resolves. Without also comparing the ownership
        // epoch this run captured at establishment, the retired batch would
        // read isProcessing=true (owned by the replacement, not this run),
        // sail past this guard, and call play() on its detached old element
        // concurrently with the replacement's audio. Note: do NOT compare
        // stopEpoch here — the "preserve playing player_voice echo across a
        // new-turn interrupt" path (stopQueuePlayback's early return) bumps
        // stopEpoch unconditionally while deliberately leaving isProcessing
        // and playbackOwnershipEpoch untouched, so it must keep passing.
        if (
          !sessionRef.current.isProcessing
          || sessionRef.current.playbackSuppressed
          || sessionRef.current.playbackOwnershipEpoch !== establishedOwnershipEpoch
        ) {
          const revokedByReplacement =
            sessionRef.current.isProcessing
            && !sessionRef.current.playbackSuppressed
            && sessionRef.current.playbackOwnershipEpoch !== establishedOwnershipEpoch;
          console.log(
            `🎵 [USER_QUEUE] Aborting chunk ${i + 1}/${chunks.length} before play — ${
              revokedByReplacement
                ? 'ownership reclaimed by a replacement processor mid-download'
                : sessionRef.current.playbackSuppressed
                  ? 'playback suppressed mid-download'
                  : 'playback cancelled'
            }`
          );
          // BOU-2912 (review P1): a named-card stop can land while this chunk is
          // still awaiting its blob download. It was already dequeued, so the
          // stop's queued-chunk loop never saw it — releasing it here (the old
          // behavior) left its row un-acked and its request unsuppressed, so the
          // backend could re-serve the chunk or its never-fetched siblings in the
          // next scene: the ghost-replay defect, surviving specifically on slow
          // downloads / quick advances. When a stop happened during this chunk
          // (stopEpoch moved) and it named this chunk's card, abandon the row
          // (request-scoped suppression) and keep it deduped; everything else
          // keeps the release semantics (suppression gates carry no card).
          const preplayStopCardId = namedStopCardIdAfterEpoch(
            sessionRef.current,
            chunkStopEpoch,
          );
          // BOU-2266 (review P2): a new-turn stop can preserve THIS chunk by
          // copying it into playbackQueue (stopQueuePlayback's
          // preserveQueuedPlayerVoice path, which includes the in-flight
          // "currently preparing" chunk) before this awaited download settles.
          // That copy is already dedup-registered in queuedChunkIds — releasing
          // it here (the pre-existing behavior) would let a queue GET that
          // resolves afterward re-register + re-append the same server row
          // behind its preserved copy, playing the player's echo twice. Mirror
          // the sibling AbortError-path guards below (~line 2340/2385): a
          // chunk that is still queued or already owned by a replacement's
          // active batch stays dedup-registered instead of being released
          // (isPreservedElsewhere, defined above).
          let abandonedAny = false;
          if (queueId && preplayStopCardId && chunk?.card_id === preplayStopCardId) {
            enqueueAck('abandoned', queueId);
            abandonedAny = true;
          } else if (!isPreservedElsewhere(chunk)) {
            releaseQueueId(queueId);
          }
          for (let j = i + 1; j < chunks.length; j++) {
            const tailChunk = chunks[j];
            const tailQueueId = tailChunk?.queue_id;
            if (!tailQueueId) continue;
            if (preplayStopCardId && tailChunk?.card_id === preplayStopCardId) {
              enqueueAck('abandoned', tailQueueId);
              abandonedAny = true;
            } else if (!isPreservedElsewhere(tailChunk)) {
              sessionRef.current.queuedChunkIds.delete(String(tailQueueId));
            }
          }
          if (abandonedAny) flushAcks();
          return false;
        }

        // BOU-780 followup: kick off a background fetch for the NEXT chunk's
        // audio file so the browser cache is warm by the time we read it
        // synchronously above for that chunk. fire-and-forget — we discard
        // the response; the browser caches the resource. Byte preload only —
        // the next-BATCH discovery for the first-segment gap (BOU-1652) is
        // triggered from the `playing` event below, not here, so it never runs
        // on the autoplay-blocked path (see onPlaying).
        const nextChunk = chunks[i + 1];
        if (nextChunk?.url) {
          let preloadUrl = nextChunk.url.startsWith('http')
            ? nextChunk.url
            : `${API_CONFIG.BACKEND_URL}${nextChunk.url}`;
          if (authToken) {
            preloadUrl += `?token=${encodeURIComponent(authToken)}`;
          }
          fetch(preloadUrl, { method: 'GET', cache: 'force-cache' }).catch(() => {
            // Preload best-effort; the real fetch on the next iteration
            // will surface any actual error.
          });
        }
        if (playbackEngine === 'element') {
          try {
            audio.currentTime = 0;
          } catch {
            // Some browsers only allow seeking after metadata loads.
          }
        }
        // Volume + mute are owned by the reactive useEffect above, which
        // re-runs whenever audioStream.volumeLevel / .isMuted changes.
        // Reassigning them here would re-read a stale closure value — the
        // bug fixed in BOU-752 — so we leave the live element alone.

        // Wait for chunk to finish playing (with watchdog + hard timeout).
        // Delegates to the AudioPlayer primitive; session-specific callbacks
        // handle the BOU-1609/BOU-1275/BOU-1652 instrumentation inline.
        // BOU-1809: the same callbacks drive both the gapless Web Audio path and
        // the HTMLAudioElement fallback, so they are defined once here.
        const chunkCallbacks = {
          onEnded: () => {
            if (queueId && sessionRef.current.currentPlaybackQueueId === queueId) {
              sessionRef.current.currentPlaybackGroup = null;
              sessionRef.current.currentPlaybackQueueId = null;
            }
            // BOU-1609: stamp the `ended` moment so the NEXT chunk's `playing`
            // can measure the inter-segment gap the player actually hears.
            if (typeof performance !== 'undefined') {
              sessionRef.current.lastChunkEndedAt = performance.now();
            }

            // BOU-1852: the played-ACK + per-request completion MUST fire when the
            // segment actually ENDS, not when it starts. The HTMLAudioElement path
            // resolves playChunk on `ended`, so this bookkeeping historically ran at
            // segment end. BOU-1809's gapless path resolves playChunkGapless at
            // segment START (so the next segment decodes ahead), so running the same
            // bookkeeping after the play `await` fired it one whole sentence early:
            // the card's LAST segment got acked played the instant it started, and
            // the played-ACK DRIVES playback_advance (see enqueueAck) — so the
            // backend served the next card and cut off the tail sentence, while the
            // reveal/advance machinery led the audible audio by a sentence (the card
            // lagged). Firing from onEnded restores end-of-segment timing for BOTH
            // paths (gaplessScheduler source.onended for gapless; the element
            // `ended`). A segment stopped early never fires onended (the scheduler
            // nulls it in stop()), so a cut segment is correctly NOT acked played.
            //
            // BOU-780 followup: fire-and-forget mark-played (batched via enqueueAck,
            // BOU-913). Do NOT release queueId after a successful play — the backend's
            // played_at may lag several hundred ms; releasing it from the dedup set
            // would let a racing fetch re-serve and replay it. queue_ids are
            // server-issued and never reused.
            enqueueAck('played', chunk.queue_id);
            // BOU-1852 (review): this segment reached true end and was just acked,
            // so it no longer needs the stop-time safety ack in stopQueuePlayback.
            if (chunk.queue_id) {
              sessionRef.current.startedGaplessQueueIds?.delete(String(chunk.queue_id));
            }

            // Inc-2: per-request completion tracking (Map, cross-batch, deduped).
            // Fire onRequestComplete once per request_id when all chunks are played.
            // Completion requires a declared positive total_chunks; while it is
            // null/0/unknown (request still generating) we accumulate the played
            // count but do NOT fire — a batch-sibling count would advance the driver
            // cursor before the rest of the card's audio plays.
            const chunkRequestId = chunk.request_id;
            if (chunkRequestId && !sessionRef.current.requestCompletedIds.has(chunkRequestId)) {
              const prev = sessionRef.current.requestPlayedChunks.get(chunkRequestId) ?? 0;
              const next = prev + 1;
              sessionRef.current.requestPlayedChunks.set(chunkRequestId, next);
              const totalForRequest = (typeof chunk.total_chunks === 'number' && chunk.total_chunks > 0)
                ? chunk.total_chunks
                : null;
              if (totalForRequest !== null && next >= totalForRequest) {
                sessionRef.current.requestCompletedIds.add(chunkRequestId);
                console.log(`🎵 [USER_QUEUE] Request ${chunkRequestId} complete (${next}/${totalForRequest} chunks played)`);
                pbTrace('request_complete', {
                  requestId: chunkRequestId,
                  cardId: chunk.card_id ?? null,
                  group: chunk.playback_group ?? null,
                  chunks: `${next}/${totalForRequest}`,
                });
                if (onRequestCompleteRef.current) {
                  // Pass richer payload: { requestId, cardId, playbackGroup }
                  // so callers can filter by playbackGroup (e.g. ignore player_voice).
                  onRequestCompleteRef.current({
                    requestId: chunkRequestId,
                    cardId: chunk.card_id ?? null,
                    playbackGroup: chunk.playback_group ?? null,
                  });
                }
              }
            }
          },
          onBlocked: () => {
            setAudioBlocked(true);
            // BOU-1275: suppress the post-playback refetch loop until the
            // user interacts (see sessionRef.current.autoplayBlocked declaration).
            sessionRef.current.autoplayBlocked = true;
          },
          onPlaying: () => {
            chunkStarted = true;
            sessionRef.current.activePlaybackStarted = true;
            sessionRef.current.currentPlaybackGroup = chunk.playback_group ?? null;
            sessionRef.current.currentPlaybackQueueId = queueId;
            // BOU-1925: a chunk is audibly playing → autoplay is unblocked, so the
            // dequeue-time lookahead is safe from here on.
            sessionRef.current.firstPlaySucceeded = true;
            // Reveal-on-audio-start: now that this chunk's audio has ACTUALLY
            // started playing (play() resolved → audible), signal the first time a
            // NEW card begins so the driver reveals it WHILE it is narrated. Gated
            // to once per card_id transition. Firing here (not at dequeue) keeps the
            // local reveal/unlock synchronized with audible audio. [Codex 3462051237]
            const startCardId = chunk.card_id ?? null;
            if (startCardId && sessionRef.current.lastAudioStartCardId !== startCardId) {
              sessionRef.current.lastAudioStartCardId = startCardId;
              if (onCardAudioStartRef.current) {
                onCardAudioStartRef.current({
                  cardId: startCardId,
                  requestId: chunk.request_id ?? null,
                  playbackGroup: chunk.playback_group ?? null,
                });
              }
            }
            // BOU-1609: inter-segment gap actually experienced by the player —
            // wall time from the previous chunk's `ended` to this chunk's
            // `playing`. This is the client-perceived stutter (the gap the
            // server-side cadence / ordering-gate / poll instrumentation is
            // meant to explain). Only meaningful between two real chunks, so
            // skip when there was no prior `ended` (first chunk of a run).
            if (
              typeof performance !== 'undefined' &&
              sessionRef.current.lastChunkEndedAt != null
            ) {
              const interSegmentGapMs = Math.round(
                performance.now() - sessionRef.current.lastChunkEndedAt
              );
              logAudioTiming('inter-segment gap', {
                gapMs: interSegmentGapMs,
                chunk: `${i + 1}/${chunks.length}`,
                cardId: chunk.card_id?.slice(-8) ?? null,
                seq: chunk.sequence_number ?? null,
              });
              // BOU-1654: observability — inter-chunk gap measurement (targets the
              // campaign-872 "first words then delay" symptom), plus an extra
              // breadcrumb when the gap is large enough to be audibly noticeable.
              recordAudioMeasurement('audio.inter_chunk_gap', interSegmentGapMs, {
                requestId: chunk.request_id ?? null,
                seq: chunk.sequence_number ?? null,
              });
              if (interSegmentGapMs > 250) {
                addAudioBreadcrumb('chunk-gap', {
                  gapMs: interSegmentGapMs,
                  requestId: chunk.request_id ?? null,
                  cardId: chunk.card_id ?? null,
                  seq: chunk.sequence_number ?? null,
                });
              }
              sessionRef.current.lastChunkEndedAt = null;
            }
            // BOU-1275: emit TTFA measurement once on the first playing event
            // after an audio_available wake. Consume and clear the mark so
            // subsequent chunks in the same turn do not re-emit.
            if (sessionRef.current.ttfaStartMark && typeof performance !== 'undefined' && performance.measure) {
              try {
                const m = performance.measure('ttfa.client_first_playing_ms', sessionRef.current.ttfaStartMark);
                const ttfaMs = Math.round(m.duration);
                console.info(`🎵 [TTFA] audio_available → first playing: ${ttfaMs}ms`);
                // BOU-1654: promote the existing TTFA mark to a Sentry measurement,
                // once per request (the mark is cleared right below).
                recordAudioMeasurement('audio.ttfa', ttfaMs, {
                  requestId: chunk.request_id ?? null,
                  cardId: chunk.card_id ?? null,
                  // TTFA decomposition: separates "the pipeline was slow" from
                  // "the wake raced the queue and we waited for a retry".
                  fetchAttempts: sessionRef.current.ttfaFetchAttempts,
                  firstFetchHit: sessionRef.current.ttfaFirstFetchHit,
                });
              } catch {
                // Defensive: start mark may have been GC'd in some browsers.
              }
              sessionRef.current.ttfaStartMark = null;
              sessionRef.current.ttfaFetchAttempts = 0;
              sessionRef.current.ttfaFirstFetchHit = null;
            }
            // BOU-1652: look ahead one step from the chunk that just STARTED
            // playing. "Keep the pipe full" — if nothing is buffered after the
            // current chunk (i is the last of this batch and the flat queue is
            // empty) and more audio is still expected, discover + fetch the next
            // batch NOW, during playback, so it appends behind us
            // (fetchUserAudioQueue's isProcessing path) and the processor plays
            // it with no gap. The turn's first audio_available often delivers
            // only segment 0, so without this the only thing that discovers
            // segment 1 is the post-playback resync — which fires AFTER segment 0
            // ENDS — i.e. the audible "first segment, then a pause" gap. As each
            // played chunk becomes the last buffered one this chains.
            //
            // Triggered from `playing` (not the pre-play preload block) on
            // purpose (review C3408323695): `playing` only fires after play()
            // SUCCEEDS, so a chunk that rejects with NotAllowedError (autoplay
            // blocked) never kicks a lookahead — which would otherwise append
            // segments the NotAllowedError handler doesn't release, leaving them
            // filtered as duplicates on the unlock-and-resume.
            //
            // "More audio expected" is NOT card-level has_more alone (review
            // C3408343362): a narrator response is usually a single card whose
            // has_more is already false while its later same-card segments are
            // still generating. We also treat the request as still producing when
            // the chunk we just started belongs to a not-yet-completed request,
            // marking a bounded producing window (audioStillExpected()). If the
            // next segment isn't ready yet the fetch returns empty and re-arms the
            // SHORT in-playback poll (review C3408323694), so it is picked up well
            // before segment 0 ends rather than on the post-`ended` resync.
            //
            // Safe + minimal: reuses the proven mid-playback fetch path
            // (BOU-1275) — coalesced (isFetching/pendingFetch) and deduped
            // (registerQueueId), so a redundant call no-ops. No TTFA impact.
            // Manual-mode cross-card lookahead stays held by the BOU-1429 gate.
            // BOU-1652 (reopened): the request-status heuristic is not enough. A
            // FAST narrator TTS finishes generating before segment 0 even starts
            // playing, so by serve time the request is already terminal
            // ('generated', total_chunks set) — NOT in REQUEST_PRODUCING_STATUSES.
            // That silently disabled the lookahead for the common case and
            // reintroduced the first-segment gap (prod campaign 831). The
            // deterministic signal the client already carries is total_chunks vs
            // sequence_number: playing seq N of a T-chunk request means segments
            // N+1..T-1 still have to be fetched, regardless of generation status.
            // Drives the lookahead off that count and OR's in the legacy
            // still-generating signal (covers the case where total_chunks isn't
            // set yet because generation is mid-flight). Terminates cleanly on the
            // last segment (seq === total - 1 → false).
            const seq = Number(chunk.sequence_number);
            const total = Number(chunk.total_chunks);
            const moreSegmentsInRequest =
              Number.isFinite(seq) && Number.isFinite(total) && total > 0 && seq < total - 1;
            const requestProducing =
              moreSegmentsInRequest
              || (!!chunk.request_status && REQUEST_PRODUCING_STATUSES.has(chunk.request_status));
            // Active-playback flag: keeps the retry alive for this segment's whole
            // lifetime (review C3408397098). The producingUntil timestamp is the
            // bounded post-drain bridge for short segments.
            sessionRef.current.activeRequestProducing = requestProducing;
            if (requestProducing && typeof performance !== 'undefined') {
              // BOU-2270: size the window by how sure we are. `moreSegmentsInRequest`
              // is derived from this chunk's own seq/total_chunks, so it is a fact —
              // segments N+1..T-1 exist and have to be fetched. The status heuristic
              // is a guess and keeps the short window. Terminates on its own: the
              // last segment has seq === total - 1, so the long window is not
              // re-stamped and the poll stops.
              sessionRef.current.producingUntil = performance.now() + (
                moreSegmentsInRequest ? OUTSTANDING_SEGMENT_GRACE_MS : PRODUCING_GRACE_MS
              );
            }
            const nothingBufferedAhead =
              i === chunks.length - 1 && sessionRef.current.playbackQueue.length === 0;
            if (
              nothingBufferedAhead &&
              audioStillExpectedRef.current() &&
              !sessionRef.current.playbackSuppressed
            ) {
              const lookaheadCampaignId = lastFetchCampaignIdRef.current;
              if (lookaheadCampaignId) {
                fetchUserAudioQueueRef.current?.(lookaheadCampaignId, { preserveAudioScope: true });
              }
            }
          },
        };

        // Web Audio resolves when the segment starts, allowing the loop to
        // schedule ahead. The element engine resolves at segment end. Keep that
        // contract stable for the whole batch; a capability failure requeues the
        // unstarted suffix instead of switching engines inline.
        if (playbackEngine === 'gapless') {
          if (!blobForChunk) {
            return await downgradeGaplessBatch(
              i,
              new Error('Gapless playback requires a downloaded audio blob'),
            );
          }
          try {
            const arrayBuffer = await blobForChunk.arrayBuffer();
            await playChunkGapless({
              scheduler: sessionRef.current.scheduler,
              arrayBuffer,
              chunk,
              chunkIndex: i,
              totalChunks: chunks.length,
              session: sessionRef.current,
              playGeneration,
              playStopEpoch: chunkStopEpoch,
              playbackGroup: chunk.playback_group,
              onPlaying: chunkCallbacks.onPlaying,
              onEnded: chunkCallbacks.onEnded,
            });
            // BOU-1852 (review): the segment has now STARTED (playChunkGapless
            // resolves at onStart) but its played-ACK is deferred to onEnded. Track
            // it so a stop before onEnd can still ack it — scheduler.stop() nulls
            // source.onended, so onEnded would otherwise never fire for it and the
            // AbortError path only acks the NEXT (pending) segment, not this one.
            if (chunk.queue_id) {
              sessionRef.current.startedGaplessQueueIds.set(
                String(chunk.queue_id),
                chunk.card_id ?? null,
              );
            }
          } catch (gaplessErr) {
            if (gaplessErr.name === 'AbortError') throw gaplessErr;
            return await downgradeGaplessBatch(i, gaplessErr);
          }
        } else {
          await playChunk({
            audio,
            chunk,
            chunkIndex: i,
            totalChunks: chunks.length,
            session: sessionRef.current,
            ...chunkCallbacks,
          });
        }

        if (i < chunks.length - 1) {
          // Inter-chunk pause: skip entirely when the next chunk is in the
          // same group (INTER_CHUNK_DELAY_MS=0). Group boundaries still get
          // a brief pause to mark the transition (e.g. narration → ambient).
          const currentGroup = chunk.playback_group;
          const nextGroup = chunks[i + 1]?.playback_group;
          if (currentGroup && nextGroup && currentGroup !== nextGroup) {
            if (playbackEngine === 'gapless' && sessionRef.current.scheduler?.insertGap) {
              sessionRef.current.scheduler.insertGap(INTER_GROUP_DELAY_MS);
            } else {
              await new Promise(resolve => setTimeout(resolve, INTER_GROUP_DELAY_MS));
            }
          }
        }

        // BOU-1852: the played-ACK + per-request completion moved into
        // chunkCallbacks.onEnded (above) so they fire at true segment END in BOTH
        // playback paths. Running them here — after the play `await` — fired them at
        // segment START in the gapless path (playChunkGapless resolves on start),
        // acking a card's last sentence one whole segment early and driving the
        // next-card advance over the still-audible tail. See the onEnded comment.
      } catch (playbackError) {
        console.error(`🎵 [USER_QUEUE] Playback error for chunk ${i + 1}/${chunks.length}; stopping ordered playback:`, playbackError);

        // If autoplay is blocked, stop trying - user needs to interact first.
        // The chunk is NOT broken — it is gated on a user gesture — so release
        // it from the dedup set: the user-interaction unlock handler refetches
        // and replays it once autoplay is permitted. The sessionRef.current.autoplayBlocked
        // latch keeps processQueue's resync from firing in the meantime, so the
        // released chunk does not loop on the same rejection.
        if (playbackError.name === 'NotAllowedError') {
          console.warn('🎵 [USER_QUEUE] Stopping playback - autoplay blocked. Click anywhere to enable audio.');
          // BOU-1266 followup: flag the block so processQueue's resync stays
          // its hand — refetching now would loop on the same rejection.
          sessionRef.current.autoplayBlocked = true;
          // TTFA: consume the pending mark HERE rather than leaving it for the
          // post-unlock replay. `playing` never fires for a blocked attempt, so
          // the mark would otherwise survive until the user happens to click and
          // the eventual success would record their reaction time as
          // time-to-first-audio — which is what put 13.6s and 12.6s samples in
          // the prod distribution.
          //
          // The pipeline work genuinely happened (fetch, download, decode, all
          // the way to a play() call), so the duration is real and worth keeping;
          // it just is not TTFA. Give it its own name.
          if (sessionRef.current.ttfaStartMark && typeof performance !== 'undefined' && performance.measure) {
            try {
              const blockedMeasure = performance.measure(
                'ttfa.autoplay_blocked_ms', sessionRef.current.ttfaStartMark,
              );
              recordAudioMeasurement('audio.ttfa_autoplay_blocked', Math.round(blockedMeasure.duration), {
                requestId: chunk.request_id ?? null,
                cardId: chunk.card_id ?? null,
                fetchAttempts: sessionRef.current.ttfaFetchAttempts,
                firstFetchHit: sessionRef.current.ttfaFirstFetchHit,
              });
            } catch {
              // Defensive: start mark may have been GC'd in some browsers.
            }
            sessionRef.current.ttfaStartMark = null;
            sessionRef.current.ttfaFetchAttempts = 0;
            sessionRef.current.ttfaFirstFetchHit = null;
          }
          // Release the current chunk AND the rest of this already-pulled batch
          // from the dedup set. processQueue took these out of
          // sessionRef.current.playbackQueue, so the tail chunks now live ONLY in
          // sessionRef.current.queuedChunkIds. Without releasing them, the user-interaction
          // unlock handler's refetch filters them as duplicates and never
          // re-adds them — so a first-chunk NotAllowedError would silence the
          // rest of the card/request until a full reload (review C3320690213).
          // Nothing is marked played: autoplay was merely gated on a gesture, so
          // every chunk from here onward must replay in order once unlocked.
          releaseQueueId(queueId);
          for (let j = i + 1; j < chunks.length; j++) {
            const tailQueueId = chunks[j]?.queue_id;
            if (tailQueueId) sessionRef.current.queuedChunkIds.delete(String(tailQueueId));
          }
          return false;
        }

        // AbortError = user skip / new-turn interrupt (stopQueuePlayback ran).
        // Every other rejection that reaches here is a HARD playback failure:
        // NotAllowedError was handled above, so this is a decode error (code=4),
        // a stall, or a hard timeout. In BOTH cases the current card cannot keep
        // playing in this pass, so the recovery is the same — skip the rest of
        // THIS card and let the progression advance to the next card.
        //
        // This batch was already pulled out of sessionRef.current.playbackQueue, so its tail
        // chunks live only in the dedup set now. KEEP same-card tail chunks
        // deduped (and mark them played) so the card the user just left — or
        // that failed to decode — does not replay after the SKIP_REFETCH_DELAY_MS
        // window; release downstream DIFFERENT-card chunks so the genuinely next
        // card auto-continues via the resync. The aborted/failed chunk itself
        // (index i) is mark-played + kept deduped (deliver-ack only, no replay):
        // releasing it would let the next fetch re-queue and replay the
        // just-skipped chunk, and for a hard failure that re-served a chunk the
        // browser can't decode in a tight fetch/play loop.
        //
        // BOU-1266 (review C3320690216): marking the failed chunk played is also
        // what makes recovery actually happen for a hard failure. The backend
        // reaper skips any request that still has an unplayed row for this user,
        // and the resync keeps re-serving that same dead row (filtered as a
        // duplicate by the dedup set), so leaving it UNplayed pinned the strict-
        // prefix frontier behind a chunk that can never play. Acking it clears
        // the blocker WITHOUT re-queuing the undecodable chunk, so the frontier
        // advances (the request completes, or the reaper can now fail it) and
        // later-card audio resumes.
        // BOU-1324 (review): if a teardown (user switch / unmount) bumped the
        // session generation, this AbortError is the teardown stopping the old
        // session's playback — NOT a user skip or a decode failure. Do NOT ack
        // the partially-heard chunk as played (that would advance the departed
        // user's playback state), and do not touch the already-reset dedup set.
        // Bail quietly; the new session starts clean.
        if (playbackError.name === 'AbortError' && sessionRef.current.generation !== playGeneration) {
          console.log('🎵 [USER_QUEUE] Playback aborted by session teardown — skipping acks for the old session');
          return false;
        }

        if (
          playbackError.name === 'AbortError'
          && sessionRef.current.preservePlayerVoiceUntilEnd
        ) {
          for (let j = i; j < chunks.length; j++) {
            const pendingQueueId = chunks[j]?.queue_id;
            if (pendingQueueId) {
              sessionRef.current.queuedChunkIds.delete(String(pendingQueueId));
            }
          }
          await sessionRef.current.scheduler?.whenDrained?.();
          sessionRef.current.preservePlayerVoiceUntilEnd = false;
          return true;
        }

        const skippedCardId = chunk?.card_id ?? null;
        // BOU-2912: when this abort came from a stop that NAMED a card (skip /
        // click-advance), that card's rows go out as `abandoned` — request-
        // scoped suppression covers the never-fetched siblings, which bare
        // `played` does not. A decode/element failure keeps `played`: the row
        // is consumed as unplayable but the request's later segments must
        // still be servable, which abandonment would suppress.
        const stopAbandonCardId =
          playbackError.name === 'AbortError'
            ? namedStopCardIdAfterEpoch(sessionRef.current, chunkStopEpoch)
            : null;
        let keptSameCardTail = false;
        for (let j = i + 1; j < chunks.length; j++) {
          const tailChunk = chunks[j];
          const tailQueueId = tailChunk?.queue_id;
          if (!tailQueueId) continue;
          if (isPreservedElsewhere(tailChunk)) {
            continue;
          }
          if (skippedCardId && tailChunk?.card_id === skippedCardId) {
            enqueueAck(
              stopAbandonCardId && tailChunk?.card_id === stopAbandonCardId
                ? 'abandoned'
                : 'played',
              tailQueueId,
            );
            keptSameCardTail = true;
            continue;
          }
          sessionRef.current.queuedChunkIds.delete(String(tailQueueId));
        }
        if (keptSameCardTail) flushAcks();

        if (playbackError.name === 'AbortError') {
          console.log('🎵 [USER_QUEUE] Playback aborted by stopQueuePlayback');
        } else {
          console.warn(
            `🎵 [USER_QUEUE] Chunk ${i + 1}/${chunks.length} failed (${playbackError.name}); skipping the rest of this card + advancing`
          );
        }
        // A gapless source can be scheduled across a group-boundary gap and then
        // aborted before onStart. Preserve the old wall-clock-pause behavior:
        // only acknowledge an aborted chunk if it actually became audible, and
        // release an unheard row from local dedup so a later fetch can replay it.
        const stoppedOnThisCard =
          playbackError.name === 'AbortError'
          && !chunkStarted
          && stopAbandonCardId != null
          && stopAbandonCardId === chunk?.card_id;
        const shouldAckCurrentChunk =
          playbackError.name !== 'AbortError' || chunkStarted || stoppedOnThisCard;
        if (chunk?.queue_id && shouldAckCurrentChunk) {
          enqueueAck(
            stopAbandonCardId && chunk?.card_id === stopAbandonCardId
              ? 'abandoned'
              : 'played',
            chunk.queue_id,
          );
          flushAcks();
        } else if (
          queueId
          && !isPreservedElsewhere(chunk)
        ) {
          sessionRef.current.queuedChunkIds.delete(queueId);
        }
        return false;
      } finally {
        // BOU-780 followup #7: revoke the blob URL once we're done with
        // this chunk (success or failure). The audio element is about to
        // get reassigned to the next chunk's blob URL anyway, so the old
        // one is unreferenced from this point on.
        if (blobUrlForChunk) {
          try { URL.revokeObjectURL(blobUrlForChunk); } catch { /* noop */ }
        }
      }
    }

    // playChunkGapless resolves when a segment starts so the next one can be
    // scheduled ahead. Wait for the final scheduled segment to drain before the
    // queue processor flips isPlaying=false and unlocks input.
    // [Codex] BUT only when the queue is EMPTY — if a lookahead/socket fetch
    // appended more chunks while this batch played, return WITHOUT draining so
    // processQueue immediately schedules them onto the gapless timeline. Draining
    // here would hold the loop until the current audio ends and reintroduce the
    // first-segment gap for the incremental frame-split case (segment 0 in batch 1,
    // segment 1 appended later).
    if (
      playbackEngine === 'gapless'
      && sessionRef.current.isProcessing
      && sessionRef.current.playbackQueue.length === 0
    ) {
      // BOU-1952: waiting only on timeline drain loses late queue arrivals. The
      // fetch path appends them and calls processQueue(), but the current
      // processor still owns the lock, so that call no-ops. Race the scheduler
      // against the queue itself: enqueue wakes this consumer, which returns to
      // processQueue's loop and schedules the new segment before the live
      // timeline drains. The empty check + waiter registration are synchronous,
      // so a producer cannot interleave between them.
      const queueWait = sessionRef.current.playbackQueue.waitForItems((queuedChunks) => (
        partitionQueueAtCardBoundary(
          queuedChunks,
          autoCardAdvancementRef.current,
          currentCardIndexRef.current,
        ).chunksToPlay.length > 0
      ));
      const schedulerDrain = sessionRef.current.scheduler.whenDrained();
      const outcome = await Promise.race([
        schedulerDrain.then(() => 'drained'),
        queueWait.promise.then((ready) => (ready ? 'queue-ready' : 'cancelled')),
      ]);
      queueWait.cancel();
      if (outcome === 'queue-ready') return true;
      if (outcome === 'cancelled') {
        // A new-turn interrupt preserves audible player_voice but resets the
        // wakeable queue, which cancels this waiter. Keep the owning processor
        // locked until that already-started scheduler drain completes; ordinary
        // stop/reset cancellation still exits immediately.
        if (sessionRef.current.preservePlayerVoiceUntilEnd) {
          await schedulerDrain;
          sessionRef.current.preservePlayerVoiceUntilEnd = false;
          return true;
        }
        return false;
      }
    }
    if (sessionRef.current.preservePlayerVoiceUntilEnd) {
      sessionRef.current.preservePlayerVoiceUntilEnd = false;
    }

    console.log('🎵 [USER_QUEUE] Completed sequential playback of all chunks');
    sessionRef.current.currentPlaybackGroup = null;
    sessionRef.current.currentPlaybackQueueId = null;
    return true;
  // Note: Using refs for callbacks, so no dependency on onRequestComplete/onCardAudioComplete
  // This prevents playQueueChunks from being recreated when callbacks change
  }, [audioStream, apiService, releaseQueueId, enqueueAck, flushAcks]);

  // Track last campaign ID fetched so processQueue can re-fetch after finishing
  const lastFetchCampaignIdRef = useRef(null);

  // Process queue - plays chunks one at a time from the flat global queue
  // Backend ordering is preserved - we just play in the order they were added
  const processQueue = useCallback(async () => {
    // Prevent concurrent processing using global lock
    if (sessionRef.current.isProcessing) {
      console.log('🎵 [USER_QUEUE] Queue processor already running, skipping');
      return;
    }

    sessionRef.current.isProcessing = true;
    setIsPlaying(true);
    console.log('🎵 [USER_QUEUE] Starting queue processor');
    // BOU-1324 (review): snapshot the generation so the post-playback resync in
    // the finally is skipped if a teardown (user switch / unmount) happened
    // during this run — otherwise the still-running processor would re-fetch the
    // old campaign and restart audio after the route was left / user switched.
    const procGeneration = sessionRef.current.generation;
    let procPlaybackOwnershipEpoch = sessionRef.current.playbackOwnershipEpoch;
    // Fresh run: clear any prior autoplay-blocked latch. If this run is blocked
    // again, playQueueChunks re-sets it before we reach the resync below.
    sessionRef.current.autoplayBlocked = false;
    let completedAllQueuedAudio = true;
    // BOU-1429: set when a manual-mode lookahead tail was gated this run. It must
    // NOT trigger the post-playback resync — the gated chunks were RELEASED for
    // re-serve and the user's next advance (or a poll) fetches them fresh; a
    // resync here would just re-serve + re-gate them in a tight loop.
    let gatedWaiting = false;

    try {
      // Play all chunks currently in the queue
      // New chunks may be added while we're processing - that's fine,
      // they'll be picked up in subsequent iterations
      while (sessionRef.current.playbackQueue.length > 0) {
        // BOU-1429: client-side card-boundary gate (manual advance only).
        // The flat queue is backend-ordered, so playable chunks (card_index <=
        // displayed cursor) form a prefix and the lookahead tail (card_index >
        // cursor) must not play ahead of the displayed card. In auto mode the
        // audio LEADS the display (backend-driven advance, BOU-751), so the gate
        // is disabled there — gating it would deadlock playback.
        // Snapshot + take ownership of the whole queue this iteration.
        const { chunksToPlay, gatedTail } = partitionQueueAtCardBoundary(
          sessionRef.current.playbackQueue.drain(),
          autoCardAdvancementRef.current,
          currentCardIndexRef.current,
        );
        if (gatedTail.length > 0) {
          // BOU-1429 (Codex review #1924): do NOT hold the gated lookahead
          // locally. We can't re-order it against a later fetch without the
          // backend's full sort key (playback_group rank, turn/card,
          // requested_at, sequence), and handleAdvance's stopQueuePlayback would
          // clear it on advance anyway. Instead release its dedup ids and drop
          // it; the next fetch (the user's advance, or a hasMore poll) re-serves
          // it from the backend in correct order, gated again until the cursor
          // reaches it. The next-chunk HTTP preload already warms the bytes.
          for (const gated of gatedTail) {
            if (gated?.queue_id) sessionRef.current.queuedChunkIds.delete(String(gated.queue_id));
          }
          gatedWaiting = true;
          console.log(`🎵 [USER_QUEUE] Gating ${gatedTail.length} lookahead chunk(s) behind displayed card (manual mode) — released for re-serve on advance`);
        }
        if (chunksToPlay.length === 0) {
          break; // nothing playable at the current cursor — wait for advance
        }
        if (
          sessionRef.current.preservedPlayerVoicePending
          && chunksToPlay.some((chunk) => chunk?.playback_group === 'player_voice')
        ) {
          sessionRef.current.preservedPlayerVoicePending = false;
        }
        console.log(`🎵 [USER_QUEUE] Processing ${chunksToPlay.length} chunks`);
        const completed = await playQueueChunks(chunksToPlay, (ownershipEpoch) => {
          procPlaybackOwnershipEpoch = ownershipEpoch;
        });
        if (!completed) {
          completedAllQueuedAudio = false;
          break;
        }
      }
      console.log('🎵 [USER_QUEUE] Queue processor finished');
      // BOU-1609 (review): scope inter-segment marks to ONE playback run. The
      // last chunk's `ended` left lastChunkEndedAt set; clear it now the run is
      // over so a later turn's first chunk doesn't measure the player's idle /
      // turn gap as an `inter-segment gap`.
      sessionRef.current.lastChunkEndedAt = null;
      // BOU-1609: the local queue just drained. If more audio is still expected
      // (hasMore), the player now hears silence until the next chunk is fetched
      // (post-playback resync, socket audio_available, or the ~4s empty-queue
      // poll). Stamp the drain moment so the next chunk-available mark measures
      // exactly how long the queue sat empty — distinguishing hypothesis #3
      // (client empty-queue poll gap) from upstream producer/ordering delay.
      // review: skip while gatedWaiting — a manual-mode lookahead gate stops the
      // processor intentionally (click-to-continue), so the user's reading time
      // must not be recorded as an empty-queue stall.
      if (typeof performance !== 'undefined' && hasMoreRef.current && !gatedWaiting) {
        sessionRef.current.queueDrainedAt = performance.now();
        // BOU-1609 follow-up: arm the card-boundary window on the same fact this
        // branch already established — the queue drained while more cards are
        // still expected, so the silence starting now is a card boundary, not
        // the end of the turn. Keeps schedulePollIfHasMore on the 750ms cadence
        // instead of the 4s keepalive until chunks land or the window expires.
        sessionRef.current.cardBoundaryUntil =
          sessionRef.current.queueDrainedAt + CARD_BOUNDARY_GRACE_MS;
        logAudioTiming('queue drained (more expected)', {
          at: Math.round(sessionRef.current.queueDrainedAt),
          completedAll: completedAllQueuedAudio,
        });
      }
    } catch (error) {
      console.error('🎵 [USER_QUEUE] Queue processor error:', error);
    } finally {
      const ownsProcessorState =
        sessionRef.current.generation === procGeneration
        && sessionRef.current.playbackOwnershipEpoch === procPlaybackOwnershipEpoch;
      // A stopped processor can finish after a replacement has claimed the
      // session. Only the owner may clear active-batch metadata; a retired
      // processor may still resume its preserved tail when the replacement
      // has not claimed the lock yet.
      const resumePreservedPlayerVoice =
        sessionRef.current.generation === procGeneration
        && sessionRef.current.preservedPlayerVoicePending
        && sessionRef.current.playbackQueue.length > 0
        && (ownsProcessorState || !sessionRef.current.isProcessing);
      // Release the shared lock / marker only while this exact processor still
      // owns playback. Generation guards route/user teardown; the playback epoch
      // also guards a hard stop followed by a same-generation replacement.
      if (ownsProcessorState) {
        sessionRef.current.isProcessing = false;
        sessionRef.current.currentPlaybackEngine = null;
        setIsPlaying(false);
      }

      // Re-sync from the server's authoritative last-unplayed position once the
      // processor stops. Two cases:
      //   - completed cleanly: catch chunks generated while we were playing
      //     (e.g. card 2's TTS finishes while card 1 is still playing).
      //   - BOU-1266/BOU-1275: cut short by a stall/abort (a chunk stalled and
      //     the per-chunk watchdog freed the lock, an element error, etc.).
      //     Without a resync here, the freed lock would strand the rest of the
      //     progression until the player resubmitted. Refetching re-derives
      //     from server truth and resumes the next unplayed segment.
      // A user-initiated skip is excluded: stopQueuePlayback(isSkip) sets
      // sessionRef.current.skipTimestamp and fetchUserAudioQueue ignores fetches within the
      // skip window, so we don't immediately replay what the user skipped.
      //
      // Autoplay-blocked is also excluded. When audio.play() was rejected with
      // NotAllowedError (no user gesture yet), refetching the same pending chunk
      // would re-enter processQueue and loop on the same rejection until the
      // user interacts. Leave the queue stopped; the interaction handler
      // re-fetches once audio unlocks.
      const campaignId = lastFetchCampaignIdRef.current;
      const recentlySkipped = Date.now() - sessionRef.current.skipTimestamp < SKIP_REFETCH_DELAY_MS;
      // BOU-1429: skip the resync entirely while gated — nothing new will land
      // until the user advances, which fires its own fetch. Resyncing here would
      // refetch the same deduped lookahead chunk and busy-loop on the gate.
      // BOU-1324 (review): also skip if a teardown bumped the generation during
      // this run — the provider unmounted or the user switched, so re-fetching
      // the old campaign would restart audio after the route was left.
      const tornDown = sessionRef.current.generation !== procGeneration;
      if (
        !tornDown
        && !gatedWaiting
        && ownsProcessorState
        && !resumePreservedPlayerVoice
        && campaignId
        && user
        && !recentlySkipped
        && !sessionRef.current.autoplayBlocked
      ) {
        const userId = user?.user_id || user?.email;
        if (userId) {
          // BOU-1429: the 500ms cushion only earns its keep at turn end (no more
          // audio expected) where it lets a straggler write land before we
          // refetch. While more audio is still expected (hasMore), waiting 500ms
          // is pure dead air between segments — the stutter — so refetch
          // immediately. The prior-fix invariants (skip window, autoplay-blocked
          // latch, suppression epoch) are all enforced independently above and
          // inside fetchUserAudioQueue, not via this delay.
          const delayMs = (completedAllQueuedAudio && !hasMoreRef.current) ? 500 : 0;
          console.log(
            '🎵 [USER_QUEUE] Re-syncing audio queue from server (%s)',
            completedAllQueuedAudio ? 'post-playback' : 'stall/abort recovery',
          );
          setTimeout(() => {
            // BOU-1324 (review): re-check the generation when the timer FIRES, not
            // only when it was scheduled. A teardown (unmount / user switch) during
            // the delay window bumps the generation; without this re-check the old
            // session would re-fetch the old campaign and restart audio after the
            // route was left. (The schedule-time `tornDown` guard above only covers
            // teardowns that already happened by the time the processor stopped.)
            if (sessionRef.current.generation !== procGeneration) return;
            // fetchUserAudioQueue is defined below — use the ref indirection
            // to avoid circular dependency. The deferral ensures this runs
            // after the current microtask, so sessionRef.current.isProcessing is false.
            fetchUserAudioQueueRef.current?.(campaignId, { preserveAudioScope: true });
          }, delayMs);
        }
      }
      if (resumePreservedPlayerVoice) {
        setTimeout(() => {
          if (
            sessionRef.current.generation === procGeneration
            && !sessionRef.current.isProcessing
            && sessionRef.current.playbackQueue.length > 0
          ) {
            processQueueRef.current?.();
          }
        }, 0);
      }
      if (ownsProcessorState) {
        sessionRef.current.activePlaybackChunks = null;
        sessionRef.current.activePlaybackIndex = null;
        sessionRef.current.activePlaybackStarted = false;
      }
    }
  }, [playQueueChunks, user]);

  processQueueRef.current = processQueue;

  // Ref to fetchUserAudioQueue for use in processQueue's post-playback refetch
  const fetchUserAudioQueueRef = useRef(null);

  // BOU-751: cancel any scheduled empty-queue poll. Called when chunks
  // arrive (poll succeeded) or when the queue is intentionally stopped.
  const cancelEmptyQueuePoll = useCallback(() => {
    if (sessionRef.current.emptyQueuePollTimer) {
      clearTimeout(sessionRef.current.emptyQueuePollTimer);
      sessionRef.current.emptyQueuePollTimer = null;
    }
  }, []);

  // BOU-751: if the view says more cards are still expected (hasMore),
  // schedule another fetch. The poll self-cancels when chunks arrive or
  // when hasMore flips false (the next poll fires, sees hasMore=false,
  // and just doesn't re-arm).
  // BOU-913: use a shorter interval when the socket is disconnected
  // (no audio_available wake-up events expected), longer when connected
  // (audio_available triggers immediate refetch; polling is just a fallback).
  // BOU-1652: keep retrying while more audio is expected — including a single-
  // card narrator response (has_more=false) whose later same-card segments are
  // still being produced (producing window). Use the SHORT in-playback cadence
  // whenever we're actively playing OR inside that producing window (which also
  // spans the brief drain between a short segment ending and its successor
  // arriving, review C3408343366), so a short segment 0 doesn't end before the
  // retry. The window auto-expires, so this can't busy-loop; the socket wake
  // (audio_available) is still the primary path when it fires.
  const schedulePollIfHasMore = useCallback((campaignId) => {
    if (!audioStillExpectedRef.current()) return;
    cancelEmptyQueuePoll();
    const socketConnected = getSocket()?.connected ?? false;
    // TTFA: a live wake window counts as fast — the wake already told us audio
    // exists, so the 4s keepalive is the wrong cadence for finding it.
    const fastRetry = sessionRef.current.isProcessing
      || producingWindowOpen()
      || wakeWindowOpen()
      // BOU-1609 follow-up: the card boundary itself. Every other term is dead
      // at exactly this moment (see CARD_BOUNDARY_GRACE_MS), which is why the
      // in-game inter-card gap fell to the 4s keepalive.
      || cardBoundaryWindowOpen();
    const pollMs = fastRetry
      ? IN_PLAYBACK_LOOKAHEAD_POLL_MS
      : (socketConnected ? EMPTY_QUEUE_POLL_MS : EMPTY_QUEUE_POLL_MS_DISCONNECTED);
    sessionRef.current.emptyQueuePollTimer = setTimeout(() => {
      sessionRef.current.emptyQueuePollTimer = null;
      fetchUserAudioQueueRef.current?.(campaignId, { preserveAudioScope: true });
    }, pollMs);
  }, [cancelEmptyQueuePoll, producingWindowOpen, wakeWindowOpen, cardBoundaryWindowOpen]);

  // Fetch and play user audio queue (user-scoped playback)
  // Optional params allow caller to pass card index directly (for timing-sensitive calls)
  const fetchUserAudioQueue = useCallback(async (campaignId, options = {}) => {
    if (!user || !campaignId) {
      console.warn('🎵 [USER_QUEUE] Cannot fetch audio queue: missing user or campaignId');
      return;
    }

    // BOU-1324 (review P2): campaign changed within the same mount (same user,
    // different :sessionId — react-router keeps the route mounted on a param
    // change). Tear the session down so campaign A's queue / dedup set / <audio>
    // / suppression state can't bleed into campaign B. Keying is
    // (user_id, campaignId): the user dimension is handled by the provider, this
    // is the campaign dimension. Skips the first fetch (prev null) and
    // same-campaign re-fetches.
    const prevCampaignId = lastFetchCampaignIdRef.current;
    if (prevCampaignId && prevCampaignId !== campaignId) {
      console.log('🎵 [USER_QUEUE] Campaign changed %s -> %s; resetting audio session', prevCampaignId, campaignId);
      teardownAudioSession({ preservePlaybackSuppression: true });
    }

    // Track for post-playback refetch
    lastFetchCampaignIdRef.current = campaignId;

    // BOU-1409: while a UI hold has suppressed playback (e.g. the quest-giver
    // cutscene spinner), ignore fetches entirely. The held NPC's opening must not
    // start until the gate is lowered on Continue.
    if (sessionRef.current.playbackSuppressed) {
      console.log('🎵 [USER_QUEUE] Ignoring fetch - playback suppressed (cutscene hold)');
      addAudioBreadcrumb('fetch', { outcome: 'dropped-suppressed', campaignId });
      return;
    }

    // A caller that releases a keyed combat round installs that exact durable
    // scope. Internal lookaheads/polls explicitly preserve it; an ordinary
    // external fetch without a scope clears it so post-combat narration is not
    // accidentally pinned to the final combat round. Scope mutation happens
    // only after the suppression guard: notifications received behind the AI
    // overlay cannot silently replace/clear the round that owns the hold.
    const hasExplicitAudioScope = Object.prototype.hasOwnProperty.call(
      options,
      'audioScopeRef',
    );
    const hasAudioEventScope = Object.prototype.hasOwnProperty.call(
      options,
      'audioEventScopeRef',
    );
    const audioEventScopeRef = hasAudioEventScope && options.audioEventScopeRef
      ? String(options.audioEventScopeRef)
      : null;
    if (hasAudioEventScope && isCombatRoundAudioScope(audioEventScopeRef)) {
      // A combat producer notification is only a wake signal. It never grants
      // release authority: the shell must already have installed the exact
      // round scope after visual playback. This rejects late rounds even after
      // the prior scope has been cleared for ordinary post-combat narration.
      if (sessionRef.current.audioScopeRef !== audioEventScopeRef) {
        addAudioBreadcrumb('fetch', {
          outcome: 'dropped-event-scope-mismatch',
          campaignId,
          requiredAudioScopeRef: sessionRef.current.audioScopeRef,
          receivedAudioScopeRef: audioEventScopeRef,
        });
        return;
      }
    } else {
      const requestedAudioScope = hasExplicitAudioScope && options.audioScopeRef
        ? String(options.audioScopeRef)
        : null;
      if (hasExplicitAudioScope) {
        if (sessionRef.current.audioScopeRef !== requestedAudioScope) {
          sessionRef.current.audioScopeRef = requestedAudioScope;
          sessionRef.current.audioScopeEpoch += 1;
        }
      } else if (!options.preserveAudioScope && sessionRef.current.audioScopeRef !== null) {
        sessionRef.current.audioScopeRef = null;
        sessionRef.current.audioScopeEpoch += 1;
      }
    }

    // After user clicks skip, ignore fetches for a short window to let API mark chunks as played
    // This prevents the audio from immediately replaying after skip.
    //
    // BOU-1409 (Codex): the cutscene-Continue resume passes bypassSkipWindow. The
    // quest-board click set this skip window via stopLocalPlayback({isSkip:true});
    // if the cutscene becomes ready fast, Continue can land inside the 1.5s window
    // and no-op here, leaving the quest-giver opening silent. By Continue the
    // backend reset+pregen has already swapped the queue to the new NPC's rows
    // (and the suppression epoch dropped any in-flight stale fetch), so honoring
    // the skip window would only suppress the audio we explicitly want to start.
    const timeSinceSkip = Date.now() - sessionRef.current.skipTimestamp;
    if (!options.bypassSkipWindow && timeSinceSkip < SKIP_REFETCH_DELAY_MS) {
      console.log('🎵 [USER_QUEUE] Ignoring fetch - skip was %dms ago (delay: %dms)', timeSinceSkip, SKIP_REFETCH_DELAY_MS);
      return;
    }
    if (options.bypassSkipWindow) {
      addAudioBreadcrumb('fetch', { outcome: 'skip-window-bypass', campaignId });
    }

    // Prevent concurrent fetches to avoid race condition with duplicate chunks
    if (sessionRef.current.isFetching) {
      sessionRef.current.pendingFetch = { campaignId, options: { ...options } };
      console.log('🎵 [USER_QUEUE] Fetch already in progress, queued latest request');
      return;
    }

    // Prefer GAIA user_id (UUID) to match backend queue storage.
    // The backend stores audio chunks keyed by GAIA user_id, NOT Auth0 sub.
    const userId = user?.user_id || user?.email;
    if (!userId) {
      console.warn('🎵 [USER_QUEUE] Cannot fetch audio queue: no user ID available');
      return;
    }

    sessionRef.current.isFetching = true;
    // BOU-1324 (review P1): capture the session generation before the awaited GET
    // (declared out here so the finally can compare it). A reset during the await
    // bumps generation, invalidating this request. Nothing between here and the
    // await is async, so this equals the value at await-time.
    const fetchGeneration = sessionRef.current.generation;
    const fetchAudioScopeEpoch = sessionRef.current.audioScopeEpoch;
    const requiredAudioScopeRef = sessionRef.current.audioScopeRef;
    try {
      // BOU-1429: unified fetch — the SAME request in every mode. We never send
      // max_card_index; the backend always derives its one-card lookahead from
      // its own playback cursor (get_playback_state), so the queue stays warm
      // one card ahead — the gapless quest-conversation behavior. Manual mode no
      // longer suppresses the lookahead at the server; instead the client
      // play-gate in processQueue holds the lookahead chunk until the displayed
      // card advances, so audio never runs ahead of a manually-advanced card.
      //
      // Sync the gate inputs synchronously here so an advance-triggered (or the
      // first manual-mode) fetch moves them BEFORE processQueue re-evaluates the
      // gate — otherwise the currentCardIndex/autoCardAdvancement prop→ref
      // effects land a render too late. Without syncing the MODE flag, the first
      // manual fetch (or a fast cached/mock response) resolving before the
      // provider rerender would read the default autoCardAdvancement=true, treat
      // the run as auto mode, and play the server lookahead ahead of the
      // displayed card (Codex review #1924).
      if (typeof options.autoCardAdvancement === 'boolean') {
        autoCardAdvancementRef.current = options.autoCardAdvancement;
      }
      if (options.cardIndex !== undefined && typeof options.cardIndex === 'number') {
        currentCardIndexRef.current = options.cardIndex;
      }

      const url = apiUrl(`/api/audio/queue/${encodeURIComponent(userId)}/${encodeURIComponent(campaignId)}`);

      console.log(`🎵 [USER_QUEUE] Fetching audio queue for user ${userId} in campaign ${campaignId}`);
      // BOU-1409 (Codex): capture the suppression epoch BEFORE the awaited GET so a
      // hold that opens (and possibly closes) while this request is in flight can be
      // detected on resolve.
      const fetchEpoch = sessionRef.current.suppressionEpoch;
      // BOU-1652 (review C3408379957 / C3408397100): capture the stop epoch at
      // fetch START. stopEpoch advances on every stopQueuePlayback — skip, manual
      // card advance, AND teardown — so an identity change by resolve time means a
      // stop/advance landed DURING this fetch. Identity (not the skip's current
      // age) is what makes this independent of how long the GET took: a slow/
      // queued response that resolves after SKIP_REFETCH_DELAY_MS, or a manual
      // advance that has no skip window at all, must still be dropped.
      const fetchStopEpoch = sessionRef.current.stopEpoch;
      const response = await apiService.makeRequest(url, null, 'GET');

      // BOU-1409 (Codex): re-check the gate AFTER the awaited fetch. A request that
      // started before suppression was raised (e.g. a Jim poll/audio_available
      // in-flight when the quest-board selection raised the gate) passes the guard
      // above, then resolves mid-hold. The epoch comparison also catches the
      // slow-network case where the gate was already lowered again on Continue by
      // the time this stale response landed (boolean is false, but epoch moved).
      // Without this the response path would enqueue + processQueue() and restart
      // stale audio behind the spinner / after Continue.
      // BOU-1652 (review C3408343364 / C3408379957 / C3408397100): also re-apply a
      // STOP guard post-await. The new during-playback lookahead can be in flight
      // when the player skips OR manually advances the card; it already passed the
      // pre-await checks, and stopQueuePlayback only clears chunks that have
      // already arrived — so a same-card tail returned afterward would otherwise be
      // queued + played, undoing the skip/advance (backend cleanup is async and
      // can't retract a response the client already accepted). Detect via stopEpoch
      // IDENTITY (a stop/advance happened since fetch start), which covers manual
      // advance (no skip window) and slow GETs alike. bypassSkipWindow (cutscene
      // Continue resume) is still honored.
      const stoppedMidFetch =
        !options.bypassSkipWindow
        && sessionRef.current.stopEpoch !== fetchStopEpoch;
      const preservedMidFetchPlayerVoice =
        stoppedMidFetch
        && !(
          sessionRef.current.lastNonPreservingStopEpoch != null
          && sessionRef.current.lastNonPreservingStopEpoch > fetchStopEpoch
        )
        && (
          sessionRef.current.preservePlayerVoiceUntilEnd
          || sessionRef.current.lastNewTurnStopEpoch === sessionRef.current.stopEpoch
        )
          ? (response?.chunks ?? []).filter(
            (chunk) => chunk.playback_group === 'player_voice',
          )
          : null;
      const stoppedWithoutPreservedVoice =
        stoppedMidFetch
        && (!preservedMidFetchPlayerVoice || preservedMidFetchPlayerVoice.length === 0);
      if (
        sessionRef.current.playbackSuppressed
        || sessionRef.current.suppressionEpoch !== fetchEpoch
        || sessionRef.current.generation !== fetchGeneration
        || sessionRef.current.audioScopeEpoch !== fetchAudioScopeEpoch
        || stoppedWithoutPreservedVoice
      ) {
        console.log('🎵 [USER_QUEUE] Dropping fetched chunks - suppressed / stale epoch / reset session / stopped mid-flight');
        addAudioBreadcrumb('fetch', { outcome: 'dropped-epoch-invalidated', campaignId });
        return;
      }

      const unscopedResponseChunks = preservedMidFetchPlayerVoice ?? response.chunks;
      const responseChunks = requiredAudioScopeRef
        ? (unscopedResponseChunks || []).filter(
          (chunk) => chunk?.scope_ref === requiredAudioScopeRef,
        )
        : unscopedResponseChunks;
      // TTFA: count fetches that actually got a usable response while a TTFA
      // mark is pending, and remember whether the FIRST one (the wake's own)
      // returned anything. `fetchAttempts > 1` is the signature of a wake that
      // raced the queue and had to wait for a retry — the thing a bare duration
      // cannot distinguish from a genuinely slow pipeline.
      if (sessionRef.current.ttfaStartMark) {
        sessionRef.current.ttfaFetchAttempts += 1;
        if (sessionRef.current.ttfaFirstFetchHit === null) {
          sessionRef.current.ttfaFirstFetchHit = (responseChunks?.length || 0) > 0;
        }
      }
      if (
        requiredAudioScopeRef
        && (unscopedResponseChunks?.length || 0) !== responseChunks.length
      ) {
        addAudioBreadcrumb('fetch', {
          outcome: 'filtered-scope-mismatch',
          campaignId,
          requiredAudioScopeRef,
          droppedChunkCount: (unscopedResponseChunks?.length || 0) - responseChunks.length,
        });
      }
      if (response.success && responseChunks && responseChunks.length > 0) {
        if (preservedMidFetchPlayerVoice?.length) {
          sessionRef.current.preservedPlayerVoicePending = true;
        }
        console.log(`🎵 [USER_QUEUE] Received ${responseChunks.length} pending chunks`);
        addAudioBreadcrumb('fetch', { outcome: 'fetched', campaignId, chunkCount: responseChunks.length });
        // DEBUG: Log chunk order to verify backend sorting
        console.log(`🎵 [USER_QUEUE] Chunk order from backend:`, responseChunks.map(c => ({
          card_id: c.card_id?.slice(-8) || 'null',
          seq: c.sequence_number,
          url: c.url?.split('/').pop()?.slice(0, 8)
        })));

        // Track request_id for logging but DON'T interrupt - just queue behind
        const incomingRequestId = responseChunks[0]?.request_id;
        if (incomingRequestId) {
          console.log(`🎵 [USER_QUEUE] Request ID: ${incomingRequestId} (current: ${sessionRef.current.currentRequestId || 'none'})`);
          sessionRef.current.currentRequestId = incomingRequestId;
        }

        const newChunks = responseChunks.filter((chunk) => {
          const queueId = chunk.queue_id ? String(chunk.queue_id) : null;
          if (!registerQueueId(queueId)) {
            console.log(`🎵 [USER_QUEUE] Skipping duplicate chunk ${queueId}`);
            return false;
          }
          return true;
        });

        if (newChunks.length === 0) {
          console.log('🎵 [USER_QUEUE] No new chunks after de-duplication');
          // BOU-751: keep polling if more cards are still expected — the
          // dedup branch just means "nothing new this fetch", not "nothing
          // more is coming". Without this, a queue that drained while
          // waiting for chunks would never retry.
          schedulePollIfHasMore(campaignId);
          return;
        }

        // SIMPLE FLAT QUEUE: Backend returns chunks in correct order
        // (card_index, requested_at, sequence_number)
        // Frontend just appends to flat queue and plays in order - no batching
        // When not processing, replace queue. When processing, append to end.
        if (!sessionRef.current.isProcessing && !sessionRef.current.preservedPlayerVoicePending) {
          // Not playing: replace queue with the fresh, backend-ordered list.
          // BOU-1429: no local merge needed — the manual-mode gate releases its
          // lookahead tail rather than holding it, so the backend response is
          // always the single source of truth for ordering.
          sessionRef.current.playbackQueue.replace(newChunks);
        } else if (!sessionRef.current.isProcessing) {
          // BOU-2266: a new-turn interrupt may have stopped the active batch
          // while its player_voice tail is waiting to resume. Keep that local
          // echo ahead of fresh turn audio instead of replacing it.
          sessionRef.current.playbackQueue.enqueue(newChunks);
        } else {
          // Currently playing: append in order. If the gapless consumer is
          // waiting for timeline drain, enqueue wakes it immediately.
          sessionRef.current.playbackQueue.enqueue(newChunks);
          preloadAudioChunks(newChunks);
        }
        console.log(`🎵 [USER_QUEUE] Queue now has ${sessionRef.current.playbackQueue.length} chunks`);
        // BOU-1609: next-chunk-available mark. If the queue had drained while
        // more audio was expected, measure how long it sat empty before these
        // chunks landed — the dead-air the player experiences between segments.
        if (typeof performance !== 'undefined' && sessionRef.current.queueDrainedAt != null) {
          const emptyQueueGapMs = Math.round(performance.now() - sessionRef.current.queueDrainedAt);
          logAudioTiming('next chunk available after drain', {
            emptyQueueGapMs,
            newChunks: newChunks.length,
            firstCardId: newChunks[0]?.card_id?.slice(-8) ?? null,
            firstSeq: newChunks[0]?.sequence_number ?? null,
          });
          sessionRef.current.queueDrainedAt = null;
        }
        // Chunks landed; cancel any pending poll. We're back in the
        // playback loop and processQueue's post-playback retry will take
        // over once the queue drains again.
        cancelEmptyQueuePoll();
        // TTFA: the wake has been satisfied — close its retry window so the
        // normal cadence resumes instead of fast-polling for the remainder.
        sessionRef.current.wakeRetryUntil = null;
        // BOU-1609 follow-up: the boundary is over — the next card's audio is
        // in hand. Close the window so the fast cadence does not outlive the
        // silence it exists to shorten.
        sessionRef.current.cardBoundaryUntil = null;
        // Trigger queue processing (will skip if already processing)
        processQueue();
      } else {
        console.log('🎵 [USER_QUEUE] No pending audio chunks in queue');
        // BOU-751: backend returned an empty queue. If more cards are
        // still expected (e.g. the next card's audio is still
        // generating), poll again in EMPTY_QUEUE_POLL_MS so we don't
        // depend on a socket event that may be missed.
        schedulePollIfHasMore(campaignId);
      }
    } catch (error) {
      console.error('🎵 [USER_QUEUE] Failed to fetch audio queue:', error);
    } finally {
      // BOU-1324 (review P1): only release the lock / replay a queued fetch if no
      // reset happened during this request. After a reset the session was already
      // cleared (isFetching=false, pendingFetch=null) and a fresh fetch for the
      // new user/campaign may now own the lock — stomping it here would clobber
      // that in-flight fetch.
      if (sessionRef.current.generation === fetchGeneration) {
        sessionRef.current.isFetching = false;
        const pendingFetch = sessionRef.current.pendingFetch;
        if (pendingFetch) {
          sessionRef.current.pendingFetch = null;
          setTimeout(() => {
            // BOU-1324 (review): re-check the generation when the replay timer
            // FIRES. A teardown in the schedule→fire window would otherwise let
            // this replay re-fetch the old campaign/user into the reset session.
            if (sessionRef.current.generation !== fetchGeneration) return;
            fetchUserAudioQueue(pendingFetch.campaignId, pendingFetch.options);
          }, 0);
        }
      }
    }
  }, [user, processQueue, apiService, registerQueueId, schedulePollIfHasMore, cancelEmptyQueuePoll, teardownAudioSession, preloadAudioChunks]);

  // Keep ref in sync for processQueue's post-playback refetch
  fetchUserAudioQueueRef.current = fetchUserAudioQueue;

  // BOU-913: wake-on-socket — subscribe to audio_available so the hook can
  // immediately refetch when new audio lands, avoiding the 4s poll delay.
  // The poll (schedulePollIfHasMore) remains as a fallback for missed events.
  useEffect(() => {
    const handleAudioAvailable = (evt) => {
      console.debug('🎵 [USER_QUEUE] audio_available received', evt);
      // BOU-1409: a UI hold (cutscene spinner) has suppressed playback. Drop the
      // wake entirely — otherwise the backend's pregen for the held NPC would
      // re-fetch and play behind the spinner. The gate is lowered on Continue,
      // which then fetches the now-revealed conversation's audio.
      if (sessionRef.current.playbackSuppressed) {
        console.debug('🎵 [USER_QUEUE] audio_available ignored - playback suppressed');
        addAudioBreadcrumb('audio-available', { dropped: 'suppressed' });
        return;
      }
      let requiredAudioScopeRef = sessionRef.current.audioScopeRef;
      const receivedAudioScopeRef = evt?.scope_ref ?? null;
      const receivedCombatRoundScope = isCombatRoundAudioScope(receivedAudioScopeRef);
      if (
        (receivedCombatRoundScope && receivedAudioScopeRef !== requiredAudioScopeRef)
        || (requiredAudioScopeRef && !receivedCombatRoundScope)
      ) {
        // Explicitly keyed combat wakes require an already-released exact
        // scope. A generic/non-combat wake instead ends the combat scope and
        // resumes normal queue admission.
        if (requiredAudioScopeRef && !receivedCombatRoundScope) {
          sessionRef.current.audioScopeRef = null;
          sessionRef.current.audioScopeEpoch += 1;
          requiredAudioScopeRef = null;
        } else {
          console.debug(
            '🎵 [USER_QUEUE] audio_available ignored - scope mismatch (%s != %s)',
            receivedAudioScopeRef || 'missing',
            requiredAudioScopeRef || 'unreleased',
          );
          addAudioBreadcrumb('audio-available', {
            dropped: 'scope-mismatch',
            requiredAudioScopeRef,
            receivedAudioScopeRef,
          });
          return;
        }
      }
      // Bail only if audio is already queued locally — those chunks are about
      // to play, so there is nothing to wake.
      //
      // BOU-1275: do NOT bail while sessionRef.current.isProcessing is true. During
      // playback the flat queue is usually empty (processQueue moved the
      // chunks into chunksToPlay), so a newly-ready card's audio must be
      // fetched and appended NOW: fetchUserAudioQueue appends to the queue
      // while processing and the running processor picks it up on its next
      // iteration. Bailing here forced the next card to wait for the current
      // one to finish plus the 500ms post-playback refetch plus download,
      // opening avoidable gaps in multi-card narration. Concurrent fetches
      // are coalesced (sessionRef.current.isFetching/sessionRef.current.pendingFetch) and duplicate
      // chunks are de-duped (registerQueueId), so fetching mid-playback is safe.
      if (sessionRef.current.playbackQueue.length > 0) {
        addAudioBreadcrumb('audio-available', { dropped: 'queue-not-empty' });
        return;
      }
      // BOU-1275: wake on the first card's audio_available even when
      // hasMore is still false. The backend may emit audio_available before
      // the view's has_more flag flips true (first-card race), so guard only
      // on the local queue being empty, not on hasMoreRef.
      const campaignId = lastFetchCampaignIdRef.current;
      if (!campaignId) return;
      addAudioBreadcrumb('audio-available', { dropped: null });
      // BOU-1275: record the TTFA start mark on the first audio_available of a
      // turn — i.e. only when nothing is playing yet. Mid-playback wakes are
      // preload fetches for LATER cards, not the turn's first audio, so they
      // must not reset the measurement window.
      if (
        !sessionRef.current.isProcessing &&
        !sessionRef.current.ttfaStartMark &&
        typeof performance !== 'undefined' &&
        performance.mark
      ) {
        sessionRef.current.ttfaStartMark = 'bou1275-ttfa-audio-available';
        performance.mark(sessionRef.current.ttfaStartMark);
        // TTFA: start the decomposition alongside the mark, so the emitted
        // measurement can say whether this wake's own fetch found the audio or
        // whether a retry had to.
        sessionRef.current.ttfaFetchAttempts = 0;
        sessionRef.current.ttfaFirstFetchHit = null;
      }
      // TTFA: the wake is evidence audio exists. Hold the short retry cadence
      // open in case this fetch races ahead of the queue becoming visible.
      if (typeof performance !== 'undefined') {
        sessionRef.current.wakeRetryUntil = performance.now() + WAKE_RETRY_WINDOW_MS;
      }
      cancelEmptyQueuePoll(); // cancel any pending 4s timer
      fetchUserAudioQueueRef.current?.(campaignId, { preserveAudioScope: true });
    };

    // BOU-1211: on (re)connect, re-drive the audio queue from authoritative
    // server state. A `connect` event means the prior connection dropped, so
    // any in-flight playback is orphaned — reset a stranded processing lock so
    // the refetch can actually restart playback, then refetch. Without this, a
    // mid-playback reconnect leaves the per-card audio gate (PlayerViewV2
    // `audioPending`) stuck: the dropped one-shot card_audio_complete never
    // replays and the queue never re-drives, so narration goes silent and the
    // narrative drip freezes until the player submits another turn.
    const handleReconnect = () => {
      const campaignId = lastFetchCampaignIdRef.current;
      if (!campaignId) return;
      if (sessionRef.current.isProcessing) {
        // Orphaned playback from the dropped socket — clear the lock + stale
        // audio element so the refetch below can re-drive cleanly.
        stopQueuePlayback();
      }
      fetchUserAudioQueueRef.current?.(campaignId, { preserveAudioScope: true });
    };

    // getSocket() reads from the shared singleton populated by useGameSocket.
    // Re-register whenever the socket object changes (e.g. after reconnect).
    let registeredSocket = null;

    const register = () => {
      const socket = getSocket();
      if (!socket || socket === registeredSocket) return;
      if (registeredSocket) {
        registeredSocket.off('audio_available', handleAudioAvailable);
        registeredSocket.off('connect', handleReconnect);
      }
      registeredSocket = socket;
      socket.on('audio_available', handleAudioAvailable);
      socket.on('connect', handleReconnect);
    };

    // Register immediately and poll until a socket is available.
    register();
    const retryTimer = setInterval(register, 500);

    return () => {
      clearInterval(retryTimer);
      if (registeredSocket) {
        registeredSocket.off('audio_available', handleAudioAvailable);
        registeredSocket.off('connect', handleReconnect);
        registeredSocket = null;
      }
    };
  // cancelEmptyQueuePoll + stopQueuePlayback are stable useCallbacks.
  }, [cancelEmptyQueuePoll, stopQueuePlayback]);

  return {
    fetchUserAudioQueue,
    playQueueChunks,
    stopQueuePlayback,
    setPlaybackSuppressed,
    teardownAudioSession,
    audioBlocked,
    isPlaying,
    unlockAudio: recoverAudio,
  };
}
